import { useState, useEffect, useRef, useMemo, useCallback, useLayoutEffect, Fragment, lazy, Suspense } from 'react';
import { shallow } from 'zustand/shallow';
import { DiagramDocumentProvider, useDiagramDocument } from "../contexts/DiagramDocumentContext";
import { PopupManagerProvider } from "../contexts/PopupManagerContext";
import { useDiagramRealtimeWithProjection } from "../hooks/useDiagramRealtimeWithProjection";
import { useDiagramWithOptimisticPreview } from "../hooks/useDiagramWithOptimisticPreview";
import { useCompassExplorerSync } from "../hooks/useCompassExplorerSync";
import { usePositionChartSync } from "../domains/orgchart/hooks/usePositionChartSync";
import { usePeopleChartSync } from "../domains/orgchart/hooks/usePeopleChartSync";
import { classifyOrgArtifact } from "../domains/orgchart/models/orgArtifactKind";
import { useLiveChartDraftSummary } from "../domains/orgchart/hooks/useLiveChartDraftSummary";
import { useLiveChartCommandGuard } from "../domains/orgchart/hooks/useLiveChartCommandGuard";
import { AtlasDraftChipActions } from "../domains/orgchart/components/AtlasDraftChipActions";
import { useOrgAnalyticsLens } from "../domains/orgchart/hooks/useOrgAnalyticsLens";
import { POSITION_STAFFING_PROJECTION_KIND } from "../domains/orgchart/components/PositionStaffingWorkbenchPane";
import { PositionStaffingWorkbenchToggle } from "../domains/orgchart/components/PositionStaffingWorkbenchToggle";
import { AtlasModeChip } from "../domains/orgchart/components/AtlasModeChip";
import { OrgProjectionErrorNotice } from "../domains/orgchart/components/OrgProjectionErrorNotice";
import { useOrgChartRecordLinking } from "../domains/orgchart/hooks/useOrgChartRecordLinking";
import { useNetworkDesignSync } from "../hooks/useNetworkDesignSync";
import { DiagramCanvasWithTransform } from "../components/canvas/DiagramCanvasWithTransform";
import { CanvasProjectionPaneHost } from "../components/canvas/projections/CanvasProjectionPaneHost";
import { CanvasProjectionSplitHandle } from "../components/canvas/projections/CanvasProjectionSplitHandle";
import { FloatingHeader } from "../components/layout/FloatingHeader";
// import { ToolDrivenPropertiesPanel } from "../components/layout/ToolDrivenPropertiesPanel";
import { FloatingProperties } from "../components/layout/FloatingProperties";
import { ClearExampleDataBanner } from "../components/genogram/ClearExampleDataBanner";
import { BottomToolbar } from "../components/layout/BottomToolbar";
import { ZoomToolbar } from "../components/layout/ZoomToolbar";
import { shouldShowStandaloneZoomToolbar } from './canvasToolbarVisibility';
import { ShapeToolbar } from "../components/layout/ShapeToolbar";
import { TableShapeToolbar } from "../components/layout/TableShapeToolbar";
import { ConnectorToolbar } from "../components/layout/ConnectorToolbar";
import { AssuranceSelectionInspector } from "../domains/process/components/AssuranceSelectionInspector";
import { ProcessCanvasLayerSync } from "../domains/process/journey/ProcessCanvasLayerSync";
import { useDiagramStore } from "../stores/diagramStore";
import { useAuthStore } from '../stores/authStore';
import { useHistoryPreviewStore } from "../stores/historyPreviewStore";
import { usePerspectiveStore } from "../stores/perspectiveStore";
import { CommandManager } from "../core/commands/CommandManager";
import { getCommandManager } from "../hooks/useCommandManager";
import { EventBus } from "../core/events/IEventBus";
import { ToolRegistry } from "../core/tools/ToolRegistry";
import type { IToolContext } from "../core/tools/interfaces/ITool";
import { GenogramEffectEngine } from "../domains/genogram/services/GenogramEffectEngine";
// Initialize NodeMutationManager singleton early
import { getNodeMutationManager } from "../core/commands/mutations/NodeMutationManager";
import { CollaborativeCursors } from "../components/CollaborativeCursors";
import { ShapeLimitProvider } from "../components/ShapeLimitProvider";
import { UpgradeModal } from "../components/ui/UpgradeModal";
import { DemoSignupBanner } from "../components/ui/DemoSignupBanner";
import { useUpgradeModalStore } from "../stores/upgradeModalStore";

import Konva from 'konva';
import { CompositeCommand, type ICommand } from '../core/commands/ICommand';
import { CreateNodeCommand } from '../core/commands/CreateNodeCommand';
import { CreateEdgeCommand } from '../core/commands/CreateEdgeCommand';
import { useDiagramSettings } from '../hooks/useDiagramSettings';
import { useEntitlements } from '../hooks/useEntitlements';
import { useOrgChartViewStore } from '../stores/orgchartViewStore';
import { useScenariosStore } from '../core/assistant/stores/scenariosStore';
import {
  getActiveOrgContentSignature,
  readPersistedCollapsedNodeIds,
  writeOrgViewSnapshot,
} from '../domains/orgchart/services/orgViewSnapshot';
import { loadOrgChartDeps } from '../components/canvas/lib/orgChartDeps';
import {
  DEFAULT_ORG_SECONDARY_REPORTS_VISIBILITY_MODE,
  type OrgSecondaryReportsVisibilityMode,
} from '../domains/orgchart/models/secondaryReportsVisibility';
import { getOrgPersonPhotoTopCenterVisualBounds } from '../domains/orgchart/models/orgPersonPhotoAnchors';
import {
  setLine2FieldFromDiagramSettings,
  useLabelSettingsStore,
} from '../stores/labelSettingsStore';
import { useUIStore } from '../stores/uiStore';
import { useMultiCanvasViewStore } from '../stores/multiCanvasViewStore';
import { UseCaseSelectionView } from '../components/genogram/UseCaseSelectionView';
import { GenoWizardDialog } from '../components/genogram/GenoWizardDialog';
import {
  HierarchyChartWizardDialog,
  type HierarchyChartWizardChoice,
} from '../components/orgchart/HierarchyChartWizardDialog';
import {
  createConflictMapTemplate,
  createBehavioralPatternMapTemplate,
  createFusionMapTemplate,
  createGeneticHistoryMapTemplate,
  createGeneticDisorderMapTemplate,
  createHeritageRootsMapTemplate,
  createLegacyMapTemplate,
  createMedicalHistoryMapTemplate,
  createPsychosocialMapTemplate
} from '../domains/genogram/utils/useCaseTemplates';
import { TemplatePickerView, type TemplateConfig } from '../components/genogram/TemplatePickerView';
import { useMeQuery } from '../hooks/useMeQuery';
import { useFeatureFlags } from '../hooks/useFeatureFlags';
import { getGravityUserId, useAuthenticatedFetch, useDemoStatus } from '../hooks/useAuth';
import { usePostHogIdentify } from '../hooks/usePostHogIdentify';
import { OnboardingDialog } from '../components/modals/OnboardingDialog';
import { useOnboardingDialogBlocksChrome } from '../components/modals/onboardingDialogChromeGate';
import { DiagramLinkPreviewModal } from '../components/modals/DiagramLinkPreviewModal';
import { useWorkflowStore } from '../stores/workflowStore';
import {
  createCoupleTemplate,
  createNuclearFamilyTemplate,
  createBlendedFamilyTemplate,
  createHouseholdTemplate,
  createExtendedFamilyTemplate,
  createIndividualTemplate
} from '../domains/genogram/utils/templateBuilder';
import { applyLayoutToTemplate } from '../domains/genogram/utils/templateLayout';
import { SetUseCaseCommand } from '../domains/genogram/commands/SetUseCaseCommand';
import { ClearGenogramCanvasCommand } from '../domains/genogram/commands/ClearGenogramCanvasCommand';
import { ClearOrgCanvasCommand } from '../domains/orgchart/commands/ClearOrgCanvasCommand';
import { inferAssistantRoleFromUseCase } from '../domains/genogram/models/AssistantRole';
import type { UseCaseType } from '../domains/genogram/models/UseCase';

import "./App.css";
import { MainLoader } from '@/components/MainLoader';
import { CanvasLoadingOverlay } from '@/components/canvas/CanvasLoadingOverlay';
import { Logger } from '../utils/logger';
import { toast } from 'sonner';
import { useTranslation } from '../hooks/useTranslation';
import { useRdmSync } from '../hooks/useRdmSync';
import { ProcessMappingAssistantPanel } from '../domains/process/components/ProcessMappingAssistantPanel';
import { useProcessDemoOnboarding } from '../domains/process/hooks/useProcessDemoOnboarding';
import { registerFlowchartConnectors, registerBpmnConnectors } from '../domains/process/connectors';
import { Tracker } from '../domains/analytics/services/Tracker';
import { GENOGRAM_WIZARD, HIERARCHY_CHART_WIZARD } from '../domains/analytics/constants/MetricEvents';
import {
  orgViewportFitCompletedSince,
  resolveCanvasLoadingPolicy,
} from './canvasLoadingPolicy';

const appLogger = Logger.getLoggerWithPrefix('App');
const GenogramAssistantPanel = lazy(() =>
  import('../domains/genogram/components/GenogramAssistantPanel').then((module) => ({
    default: module.GenogramAssistantPanel,
  }))
);
const OrgChartAssistantPanel = lazy(() =>
  import('../domains/orgchart/components/OrgChartAssistantPanelRefactored').then((module) => ({
    default: module.OrgChartAssistantPanelRefactored,
  }))
);
const NetworkAssistantPanel = lazy(() =>
  import('../domains/network/components/NetworkAssistantPanel').then((module) => ({
    default: module.NetworkAssistantPanel,
  }))
);
import { ColorLegend } from '../domains/orgchart/components/ColorLegend';
import { ProcessOverlayLegend } from '../domains/process/components/ProcessOverlayLegend';
import { GenogramHealthFocusLegend } from '../domains/genogram/components/GenogramHealthFocusLegend';
import { getProfileForDiagramType } from '../core/data-profiles';
import { useOnboardingContext } from '../contexts/OnboardingContext';
import { computeDiagramFitToContent } from '../components/canvas/lib/computeDiagramFitToContent';
import { useStartIntentSeed } from '../core/seeds/useStartIntentSeed';
import { shouldSkipWizard } from '../core/seeds/startIntent';
import { isNetworkShapeLibraryId } from '../domains/network/shapes/networkShapeLibraryCatalog';
import { FlowchartStartPicker } from '../domains/process/components/FlowchartStartPicker';
import { ProcessUseCasePicker } from '../domains/process/components/ProcessUseCasePicker';
import { useFlowchartStartPicker } from '../domains/process/hooks/useFlowchartStartPicker';
import { resolveAssistantPanelKind } from './assistantMountPolicy';
import { resolveStartSurfacePolicy } from './startSurfacePolicy';

// Prototype: swap the flowchart "how to start" picker for the registry-driven
// process use-case picker ("what are you mapping?"). Off by default; flip to
// trial the purpose-first pattern. Productionizing = a PostHog flag so the
// use-case set + variant can be A-B'd.
const SHOW_PROCESS_USE_CASE_PICKER: boolean = false;
import { OrgChartStartPicker } from '../domains/orgchart/components/OrgChartStartPicker';
import { useOrgChartStartPicker } from '../domains/orgchart/hooks/useOrgChartStartPicker';
import { useDiagramInfo } from '../hooks/useDiagramInfo';

// Auto-open the toolbar shape picker once it has registered its global opener.
// The opener (`window.__openShapePicker`) is registered by ShapePickerTool when
// its toolbar component mounts, which can race with the canvas becoming ready.
// We retry until the opener exists, and then re-assert "open" a few times over a
// short startup window so the picker survives the noisy first-load (a transient
// chrome remount would otherwise drop the freshly-set open state). ShapePicker
// itself ignores transient closes during the same window. Returns a cleanup that
// cancels any pending timers.
function scheduleOpenShapePicker(onOpened?: () => void): () => void {
  let cancelled = false;
  const timers: number[] = [];
  let findAttempts = 0;
  const maxFindAttempts = 20;
  // Re-assert open at these offsets (ms) once the opener is available.
  const reassertOffsets = [0, 250, 500];

  const callOpener = (): boolean => {
    const openShapePicker = (window as unknown as { __openShapePicker?: () => void }).__openShapePicker;
    if (typeof openShapePicker !== 'function') return false;
    try { openShapePicker(); } catch {}
    return true;
  };

  const start = () => {
    if (cancelled) return;
    if (!callOpener()) {
      if (findAttempts >= maxFindAttempts) return;
      findAttempts += 1;
      timers.push(window.setTimeout(start, 100));
      return;
    }
    onOpened?.();
    // Opener found and invoked; re-assert across the startup window.
    for (const offset of reassertOffsets.slice(1)) {
      timers.push(window.setTimeout(() => {
        if (cancelled) return;
        callOpener();
      }, offset));
    }
  };

  timers.push(window.setTimeout(start, 0));

  return () => {
    cancelled = true;
    for (const id of timers) window.clearTimeout(id);
  };
}

function DiagramEditor() {
  // Get access information from context
  const { hasAccess, accessError, documentError, isLoadingAccess, canWrite: documentCanWrite, userRole, doc, awareness, isSynced, diagramType, isDiagramTypeResolved, diagramId } = useDiagramDocument();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const historyScope = useHistoryPreviewStore((state) => state.scope);
  const isHistoryPreview = Boolean(diagramId && historyScope?.diagramId === diagramId);
  const canWrite = documentCanWrite && historyScope?.diagramId !== diagramId;
  const { isRdmAssistantWriteEnabled, isAtlasWorkloadPlanningEnabled, loading: featureFlagsLoading } = useFeatureFlags();
  const { entitlements, loading: entitlementsLoading } = useEntitlements(diagramId || undefined);
  const demoStatus = useDemoStatus();
  const isDemoUser = demoStatus?.toUpperCase() === 'DEMO';
  const canShowOrgProjectionErrorNotice = !entitlementsLoading && entitlements?.planType !== 'free' && !isDemoUser;
  const showAssistant = !featureFlagsLoading && isRdmAssistantWriteEnabled();
  const isGenogramDiagram = isDiagramTypeResolved && diagramType === 'genogram';
  const isFlowchartDiagram = isDiagramTypeResolved && diagramType === 'flowchart';
  const currentDiagramInfo = useDiagramInfo(diagramId || undefined);
  const currentFolderId = currentDiagramInfo.data?.folderId ?? null;
  const atlasArtifactKind = classifyOrgArtifact(currentDiagramInfo.data?.viewDefinition);
  const isPlanningChart = currentDiagramInfo.data?.viewDefinition?.projection === 'planning_chart';
  // A saved view is functionally read-only: scenario/succession authoring
  // never commits for it (the backend rejects a non-canonical diagram), so
  // launcher tools that open those workflows must not offer to either.
  const isSavedAtlasView = atlasArtifactKind === 'custom';

  useEffect(() => {
    if (!isDiagramTypeResolved || !diagramType) return;
    useUpgradeModalStore.getState().setDiagramType(diagramType);
  }, [diagramType, isDiagramTypeResolved]);

  useProcessDemoOnboarding({
    enabled: isFlowchartDiagram,
    canWrite,
    isLoadingAccess,
  });

  // Resolve domain profile and chrome config from diagram type
  const domainProfile = useMemo(() => getProfileForDiagramType(diagramType as any), [diagramType]);
  const chromeConfig = domainProfile?.chromeConfig;
  const hiddenTools = useMemo(() => chromeConfig?.hiddenTools ?? [], [chromeConfig]);

  // Compass Explorer sync — no-op unless this diagram is an explorer
  // marker. When it is, this hook supersedes the Yjs projection below
  // by computing the scene from the folder graph + focus/zoom state
  // and calling loadScene(). Focus controls live in the network
  // assistant panel; see NetworkAssistantPanel > View & Display.
  const { isExplorer } = useCompassExplorerSync(diagramId);
  const positionChart = usePositionChartSync(diagramId, doc);
  const { isPositionChart } = positionChart;
  const peopleChart = usePeopleChartSync(diagramId, doc);
  // `?lens=` colours the live chart with an analytics preset resolved for this
  // viewer. Transient — never written to the shared document.
  useOrgAnalyticsLens({
    diagramId,
    folderId: peopleChart.folderId,
    enabled: peopleChart.isPeopleChart && !peopleChart.isSceneScoped,
  });
  const { isPeopleChart } = peopleChart;
  // A selected draft keeps command-guard context, but live projection geometry
  // unlocks only while the scenario lifecycle is explicitly being edited.
  const hasActiveCanvasDraft = useScenariosStore((state) => state.activeScenarioId != null);
  const isEditingCanvasDraft = useScenariosStore(
    (state) => state.scenarioState === 'EDITING_SCENARIO',
  );

  // What the mode chip owes the user: a live chart stops being a "read-only
  // view of current data" the moment a draft overlays it, and the chip is the
  // one surface that says which reality the canvas is showing.
  const atlasFolderId = isPositionChart
    ? positionChart.folderId
    : peopleChart.folderId ?? currentFolderId;
  const liveChartDraftSummary = useLiveChartDraftSummary({
    folderId: atlasFolderId,
    diagramId,
    enabled: (isPeopleChart || isPositionChart || isPlanningChart) && !isSavedAtlasView,
  });
  const liveChartDraftChangeCount = liveChartDraftSummary.hasDraft
    ? liveChartDraftSummary.draftChangeCount
    : null;
  const liveChartDraftActions = liveChartDraftChangeCount != null ? (
    <AtlasDraftChipActions
      folderId={atlasFolderId}
      diagramId={diagramId}
      canWrite={canWrite}
      showPlanningDiagramAction={atlasArtifactKind === 'live'}
      hasClientDraft={liveChartDraftSummary.hasClientDraft}
      clientChangeCount={liveChartDraftSummary.clientChangeCount}
      viewDefinition={currentDiagramInfo.data?.viewDefinition}
      isPlanningChart={atlasArtifactKind === 'planning'}
    />
  ) : null;

  // Org record linking — binds fresh org-chart nodes to folder records
  // (attrs.folderRecordId) so record-gated surfaces like succession
  // drafting can enable. No-op for non-org diagrams and projection views.
  useOrgChartRecordLinking(diagramId);

  // Network design-mode hydration — DL-1. When the diagram is a
  // `network-design` fork and the Y.Doc is empty (first open after
  // ForkAsDesignService created it), this hook fetches the baseline
  // folder graph and seeds the diagram via the standard import path
  // (CreateNetworkResourceCommand → CommandManager → Y.Doc). A
  // sentinel in Y.Doc makes the seed idempotent across collaborators.
  // Coexists with the regular Yjs projection — once seeded, normal
  // collaborative editing applies.
  useNetworkDesignSync(diagramId);

  // Set up Yjs → Zustand projection (syncs Yjs doc to Zustand store for canvas rendering).
  // Disabled for Compass Explorer diagrams — the explorer synthesises its scene
  // client-side and the Yjs doc is deliberately empty; running projection would
  // race with loadScene() and intermittently blank the canvas.
  useDiagramRealtimeWithProjection({
    diagramType: diagramType as any,
    enabled: !isExplorer && !isPositionChart && !isPeopleChart,
  });

  // Register BPMN connectors early when flowchart - ensures ConnectorToolbar can change connector types
  useEffect(() => {
    if (diagramType === 'flowchart') {
      registerFlowchartConnectors();
      registerBpmnConnectors();
    }
  }, [diagramType]);

  // RDM live sync - connects to local RDM viewer server when ?rdm-sync=true
  useRdmSync();

  // Initialize awareness state if it's null (but don't clear existing state)
  useEffect(() => {
    if (awareness && awareness.getLocalState() === null) {
      awareness.setLocalState({});
    }
  }, [awareness]);
  
  // Add optimistic preview support
  const {
    updatePreview,
    getShapeState,
    setUserSelection,
    userInfo
  } = useDiagramWithOptimisticPreview({
    doc,
    awareness,
  });
  // Persist/load perspective palettes via Yjs
  const { read: readSettings, write: writeSettings } = useDiagramSettings(doc);
  // Auto-apply use case from deep-link onboarding (URL → sessionStorage → OnboardingContext)
  const onboarding = useOnboardingContext();
  const didApplyNetworkShapeSetRef = useRef(false);
  const didApplyManualOrgStartRef = useRef(false);

  useEffect(() => {
    setLine2FieldFromDiagramSettings(undefined);
  }, [doc]);

  // Seed the org collapse state at mount, from localStorage, before Yjs syncs.
  // The post-sync restore below is too late: nodes hydrate around the same
  // moment, so the projection's auto-collapse can run first, paint the whole
  // uncollapsed tree and then overwrite the user's saved expand/collapse
  // choices with a freshly computed default overview. Seeding here means the
  // very first projection that sees nodes is already collapsed — the hidden
  // subtrees never reach Konva at all.
  const didHydrateOrgViewRef = useRef<string | null>(null);
  useLayoutEffect(() => {
    if (!diagramId || didHydrateOrgViewRef.current === diagramId) return;
    const collapsed = readPersistedCollapsedNodeIds(diagramId);
    didHydrateOrgViewRef.current = diagramId;
    if (collapsed) {
      useOrgChartViewStore.getState().setCollapsedNodeIds(collapsed);
    }
  }, [diagramId]);

  // Start loading the org projection modules the moment we know this is an org
  // chart, in parallel with the realtime sync. They used to be requested only
  // once the canvas host mounted, which lost the race against node hydration:
  // until they land the canvas has no projection to render and falls back to
  // the raw store scene, so the whole uncollapsed chart paints and is then torn
  // back down to the overview. The loader dedupes, so calling it early is free.
  useEffect(() => {
    if (diagramType !== 'org') return;
    void loadOrgChartDeps().catch(() => { /* the canvas host retries */ });
  }, [diagramType]);

  useEffect(() => {
    if (!doc || !isSynced) return;
    const settings = readSettings();
    const { setCultureColor, setHealthItem, setPerspective } = usePerspectiveStore.getState();
    if (settings.palette?.culture) {
      Object.entries(settings.palette.culture).forEach(([tag, color]) => setCultureColor(tag, color as string));
    }
    if (settings.palette?.health) {
      Object.entries(settings.palette.health).forEach(([tag, item]) => setHealthItem(tag, item as { color: string; icon?: string; propagate?: boolean }));
    }
    if (settings.perspectiveDefault) {
      setPerspective(settings.perspectiveDefault);
    }
    setLine2FieldFromDiagramSettings(settings.label?.line2Field);
    // Load persisted org chart collapse state (Y.Doc first, localStorage fallback)
    let restoredCollapsed = settings.orgchart?.collapsedNodeIds;
    if (!restoredCollapsed || restoredCollapsed.length === 0) {
      try {
        const lsKey = `creately-collapse-${diagramId}`;
        const lsData = localStorage.getItem(lsKey);
        if (lsData) restoredCollapsed = JSON.parse(lsData);
      } catch { /* ignore parse errors */ }
    }
    if (restoredCollapsed && restoredCollapsed.length > 0) {
      // The mount-time hydration above has usually already applied exactly
      // this set. Re-setting it would hand the projection a new array identity
      // and re-run the whole layout/fit pipeline for no change, so only apply
      // when the Y.Doc actually carries something different (cross-device).
      const current = useOrgChartViewStore.getState().collapsedNodeIds;
      const unchanged =
        current.length === restoredCollapsed.length &&
        current.every((id, index) => id === restoredCollapsed![index]);
      if (!unchanged) {
        useOrgChartViewStore.getState().setCollapsedNodeIds(restoredCollapsed);
      }
    }
    let restoredAsOfDate = settings.orgchart?.asOfDate;
    if (!restoredAsOfDate) {
      try {
        const lsKey = `creately-asOfDate-${diagramId}`;
        const lsData = localStorage.getItem(lsKey);
        if (lsData) restoredAsOfDate = JSON.parse(lsData);
      } catch { /* ignore */ }
    }
    if (restoredAsOfDate) {
      useOrgChartViewStore.getState().setAsOfDate(restoredAsOfDate);
    }
    if (settings.orgchart?.focusScope && settings.orgchart.focusScope.type !== 'all') {
      useOrgChartViewStore.getState().setFocusScope(settings.orgchart.focusScope);
    }
    if (typeof settings.orgchart?.autoLayoutEnabled === 'boolean') {
      useOrgChartViewStore.getState().setAutoLayoutEnabled(settings.orgchart.autoLayoutEnabled);
    }
    if (typeof settings.orgchart?.nodeSpacing === 'number') {
      useOrgChartViewStore.getState().setNodeSpacing(settings.orgchart.nodeSpacing);
    }
    if (typeof settings.orgchart?.verticalSpacing === 'number') {
      useOrgChartViewStore.getState().setVerticalSpacing(settings.orgchart.verticalSpacing);
    }
    const persistedSecondaryReportsVisibilityMode =
      settings.orgchart?.secondaryReportsVisibilityMode;
    const resolvedSecondaryReportsVisibilityMode: OrgSecondaryReportsVisibilityMode =
      persistedSecondaryReportsVisibilityMode === 'hidden' ||
      persistedSecondaryReportsVisibilityMode === 'selection' ||
      persistedSecondaryReportsVisibilityMode === 'always'
        ? persistedSecondaryReportsVisibilityMode
        : settings.orgchart?.showSecondaryReportsOnSelection === true
          ? 'selection'
          : DEFAULT_ORG_SECONDARY_REPORTS_VISIBILITY_MODE;
    useOrgChartViewStore
      .getState()
      .setSecondaryReportsVisibilityMode(resolvedSecondaryReportsVisibilityMode);
    if (diagramType === 'org') {
      useOrgChartViewStore.getState().requestViewportFit();
    }
    // Load persisted workflow state
    if (settings.workflow?.selectedUseCase !== undefined) {
      import('../core/commands/mutations/WorkflowMutationManager').then(({ getWorkflowMutationManager }) => {
        const manager = getWorkflowMutationManager();
        const useCase = settings.workflow!.selectedUseCase as any;
        const persistedRole = settings.workflow?.assistantRole as any;
        manager.resetWorkflow();
        manager.updateWorkflow({
          selectedUseCase: useCase,
          assistantRole: persistedRole ?? inferAssistantRoleFromUseCase(useCase),
        });
        // For 'blank' use case, automatically mark template as selected to skip template picker
        if (useCase === 'blank') {
          setTemplateSelected(true);
        }
        setWorkflowStateLoaded(true);
      })
        .catch(() => {
          setWorkflowStateLoaded(true);
        });
    } else {
      // No persisted workflow state, mark as loaded so we can show the selection screen
      setWorkflowStateLoaded(true);
    }
  }, [doc, isSynced, readSettings, diagramType, onboarding.genogramStartMode]);

  useEffect(() => {
    if (!doc || !isSynced || diagramType !== 'network') return;
    if (didApplyNetworkShapeSetRef.current) return;
    const shapeSet = onboarding.startIntent?.shapeSet;
    if (!isNetworkShapeLibraryId(shapeSet)) return;
    const existing = readSettings();
    if (existing.network?.defaultShapeLibrary === shapeSet) {
      didApplyNetworkShapeSetRef.current = true;
      return;
    }
    writeSettings({
      ...existing,
      network: {
        ...existing.network,
        defaultShapeLibrary: shapeSet,
      },
    });
    didApplyNetworkShapeSetRef.current = true;
  }, [doc, isSynced, diagramType, onboarding.startIntent?.shapeSet, readSettings, writeSettings]);

  useEffect(() => {
    if (!doc || !isSynced || diagramType !== 'org') return;
    if (onboarding.startView !== 'manual') return;
    if (didApplyManualOrgStartRef.current) return;
    useOrgChartViewStore.getState().resetView();
    const existing = readSettings();
    writeSettings({
      ...existing,
      orgchart: {
        ...(existing.orgchart || {}),
        focusScope: undefined,
        collapsedNodeIds: undefined,
        autoLayoutEnabled: false,
      },
    });
    didApplyManualOrgStartRef.current = true;
  }, [doc, isSynced, diagramType, onboarding.startView, readSettings, writeSettings]);

  useEffect(() => {
    const selectPalette = (state: ReturnType<typeof usePerspectiveStore.getState>) => ({
      perspective: state.perspective,
      culturePalette: state.culturePalette,
      healthPalette: state.healthPalette,
    });

    let prev = selectPalette(usePerspectiveStore.getState());

    const unsub = usePerspectiveStore.subscribe((state) => {
      const next = selectPalette(state);
      if (shallow(prev, next)) {
        return;
      }
      prev = next;
      const existing = readSettings();
      writeSettings({
        ...existing,
        perspectiveDefault: next.perspective,
        palette: {
          culture: next.culturePalette,
          health: next.healthPalette,
        },
      });
    });

    return () => { try { unsub(); } catch (e) { appLogger.warn(e); } };
  }, [doc, readSettings, writeSettings]);

  // Persist label settings
  useEffect(() => {
    const unsub = useLabelSettingsStore.subscribe((state) => {
      const existing = readSettings();
      writeSettings({
        ...existing,
        label: {
          ...(existing.label || {}),
          line2Field: state.line2Field ?? null,
        }
      });
    });
    return () => { try { unsub(); } catch (e) { appLogger.warn(e); } };
  }, [readSettings, writeSettings]);

  // Persist workflow state (selectedUseCase + assistantRole)
  useEffect(() => {
    const prev = {
      selectedUseCase: useWorkflowStore.getState().selectedUseCase,
      assistantRole: useWorkflowStore.getState().assistantRole,
    };
    const unsub = useWorkflowStore.subscribe((state) => {
      // Only write to Yjs if relevant workflow state changed
      if (state.selectedUseCase === prev.selectedUseCase && state.assistantRole === prev.assistantRole) {
        return;
      }
      prev.selectedUseCase = state.selectedUseCase;
      prev.assistantRole = state.assistantRole;
      const existing = readSettings();
      writeSettings({
        ...existing,
        workflow: {
          ...(existing.workflow || {}),
          selectedUseCase: state.selectedUseCase ?? null,
          assistantRole: state.assistantRole ?? null,
        }
      });
    });
    return () => { try { unsub(); } catch (e) { appLogger.warn(e); } };
  }, [readSettings, writeSettings]);

  // Persist org chart collapse state and focus scope
  useEffect(() => {
    let prevCollapsed = useOrgChartViewStore.getState().collapsedNodeIds;
    let prevFocusScope = useOrgChartViewStore.getState().focusScope;
    let prevAsOfDate = useOrgChartViewStore.getState().asOfDate;
    let prevAutoLayoutEnabled = useOrgChartViewStore.getState().autoLayoutEnabled;
    let prevSecondaryReportsVisibilityMode =
      useOrgChartViewStore.getState().secondaryReportsVisibilityMode;
    const lsKey = `creately-collapse-${diagramId}`;
    const lsAsOfKey = `creately-asOfDate-${diagramId}`;
    const unsub = useOrgChartViewStore.subscribe((state) => {
      const collapsedChanged = state.collapsedNodeIds !== prevCollapsed;
      const focusChanged = state.focusScope !== prevFocusScope;
      const asOfDateChanged = state.asOfDate !== prevAsOfDate;
      const autoLayoutChanged = state.autoLayoutEnabled !== prevAutoLayoutEnabled;
      const secondaryReportsVisibilityChanged =
        state.secondaryReportsVisibilityMode !== prevSecondaryReportsVisibilityMode;
      if (
        !collapsedChanged &&
        !focusChanged &&
        !asOfDateChanged &&
        !autoLayoutChanged &&
        !secondaryReportsVisibilityChanged
      ) {
        return;
      }
      prevCollapsed = state.collapsedNodeIds;
      prevFocusScope = state.focusScope;
      prevAsOfDate = state.asOfDate;
      prevAutoLayoutEnabled = state.autoLayoutEnabled;
      prevSecondaryReportsVisibilityMode = state.secondaryReportsVisibilityMode;
      console.log('[APP-SETTINGS-DIAG] Persisting collapse state:', {
        count: state.collapsedNodeIds.length,
        sample: state.collapsedNodeIds.slice(0, 3),
        willSave: state.collapsedNodeIds.length > 0,
      });
      try {
        if (state.collapsedNodeIds.length > 0) {
          localStorage.setItem(lsKey, JSON.stringify(state.collapsedNodeIds));
        } else {
          localStorage.removeItem(lsKey);
        }
        if (state.asOfDate) {
          localStorage.setItem(lsAsOfKey, JSON.stringify(state.asOfDate));
        } else {
          localStorage.removeItem(lsAsOfKey);
        }
      } catch { /* quota or private browsing */ }
      const existing = readSettings();
      writeSettings({
        ...existing,
        orgchart: {
          ...(existing.orgchart || {}),
          collapsedNodeIds: state.collapsedNodeIds.length > 0 ? state.collapsedNodeIds : undefined,
          focusScope: state.focusScope.type !== 'all' ? state.focusScope : undefined,
          asOfDate: state.asOfDate ?? undefined,
          autoLayoutEnabled: state.autoLayoutEnabled,
          showSecondaryReportsOnSelection: undefined,
          secondaryReportsVisibilityMode: state.secondaryReportsVisibilityMode,
        },
      });
    });
    return () => { try { unsub(); } catch (e) { appLogger.warn(e); } };
  }, [diagramId, readSettings, writeSettings]);

  // Persist the org viewport so a refresh restores where the user actually was
  // instead of re-deriving a fit. Tagged with the signature of the picture the
  // canvas is rendering — a viewport is only reusable against the same layout.
  useEffect(() => {
    if (diagramType !== 'org' || !diagramId) return;
    let timer: number | null = null;
    const unsub = useDiagramStore.subscribe((state, prev) => {
      if (state.stage === prev.stage) return;
      const stage = state.stage;
      // Signature at the moment the viewport moved. Only persist if the canvas
      // is still rendering the same picture when the debounce flushes: a fit
      // that landed on a half-assembled layout is followed by a reflow, and
      // saving that viewport would make a bad framing sticky across refreshes.
      const signatureAtChange = getActiveOrgContentSignature();
      if (!signatureAtChange) return;
      if (timer !== null) window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        timer = null;
        if (getActiveOrgContentSignature() !== signatureAtChange) return;
        writeOrgViewSnapshot(diagramId, stage, signatureAtChange);
      }, 400);
    });
    return () => {
      if (timer !== null) window.clearTimeout(timer);
      try { unsub(); } catch (e) { appLogger.warn(e); }
    };
  }, [diagramId, diagramType]);

  // Core system instances
  const eventBusRef = useRef(new EventBus());

  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  // Use shared global CommandManager so all tools and UI act on the same history
  const commandManagerRef = useRef<CommandManager>(getCommandManager());
  useEffect(() => commandManagerRef.current.addOperationGuard((command) => {
    const isHistoryPreview = useHistoryPreviewStore.getState().scope?.diagramId === diagramId;
    const canCreateAnnotation = command.executionScope === 'annotation'
      && isAuthenticated
      && hasAccess
      && !isHistoryPreview;

    return canCreateAnnotation
      || (command.executionScope === 'presentation' && !command.requiresWriteAccess)
      || (documentCanWrite && !isHistoryPreview
        && !(command.requiresWriteAccess && useOrgChartViewStore.getState().asOfDate));
  }), [diagramId, documentCanWrite, hasAccess, isAuthenticated]);
  const liveChartCommandGuardDialog = useLiveChartCommandGuard({
    commandManager: commandManagerRef.current,
    diagramId,
    folderId: atlasFolderId,
    isLiveChart: isPeopleChart || isPositionChart,
    isPlanningChart,
    hasOpenDraft: hasActiveCanvasDraft || liveChartDraftSummary.hasDraft,
  });
  const [toolPanelsVersion, setToolPanelsVersion] = useState(0);

  // Ensure the shared manager uses this app's event bus for UI wiring
  useEffect(() => {
    commandManagerRef.current.setEventBus(eventBusRef.current);
  }, []);

  // Zoom-to-fit after a seed lands. The actual fit logic depends on
  // handleZoomToFit (defined far below), so seed paths invoke this ref and an
  // effect keeps it pointed at the live fit closure — a scoped callback
  // instead of a global window event.
  const fitAfterSeedRef = useRef<() => void>(() => {});
  const handleSeeded = useCallback(() => fitAfterSeedRef.current(), []);
  const handleSeedError = useCallback((error: unknown) => {
    const message =
      error instanceof Error && error.message
        ? error.message
        : 'We could not generate this diagram. Please try again.';
    toast.error(message, { duration: 6000 });
  }, []);

  // StartIntent seeding — opens a freshly-created native diagram WITH
  // content. Sample seeds resolve through the Seed Registry (flowchart RDM,
  // org CSV provider, network template providers); agent seeds (prompt
  // start) run one agent turn and materialize the returned RDM.
  const authenticatedFetch = useAuthenticatedFetch();
  const { isGeneratingSeed } = useStartIntentSeed({
    startIntent: onboarding.startIntent,
    commandManager: commandManagerRef.current,
    ready: isSynced && canWrite && !isLoadingAccess,
    onSeeded: handleSeeded,
    onSeedError: handleSeedError,
    doc,
    diagramType,
    requestFn: authenticatedFetch,
    folderId: currentFolderId,
    eventBus: eventBusRef.current,
    dialectId: onboarding.networkDialectId,
  });
  const stageRef = useRef<Konva.Stage>(null as unknown as Konva.Stage);
  const layerRef = useRef<Konva.Layer>(null as unknown as Konva.Layer);
  const canvasContainerRef = useRef<HTMLDivElement>(null);  
  const { t: tCommon } = useTranslation('common');
  
  // Tool registry is created ONCE per editor mount. It must NOT be re-created
  // when diagramType/entitlements resolve: recreating the registry mid-session
  // orphans any in-flight async domain-plugin tool registration (the plugin's
  // registerTools resolves against the previous registry instance), so domain
  // tools (e.g. orgchart-data) can land in a dead registry while the UI reads
  // the new one — leaving live charts with a Notes-only property panel. The
  // live diagramType/entitlements/canWrite are projected in via updateContext
  // from the canvas, so the seed values below only prime the initial context.
  const toolRegistry = useMemo(() => {
    const cm = commandManagerRef.current;
    const eb = eventBusRef.current;
    const ctx = {
      selection: useDiagramStore.getState().selectedIds,
      shapes: new Map(),
      connectors: new Map(),
      frame: null,
      diagramType: diagramType as any,
      stage: stageRef.current as unknown as Konva.Stage,
      layer: layerRef.current as unknown as Konva.Layer,
      eventBus: eb,
      canWrite: false,
      entitlements,
      getSelectedShapes: () => [],
      getSelectedConnectors: () => [],
      getShapesInFrame: () => []
    } as unknown as IToolContext;
    const registry = new ToolRegistry(ctx, eb, cm, { autoActivateDefault: false, allowMultipleActive: false });
    return registry;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- stable for the editor's lifetime by design; see comment above
  }, []);

  useEffect(() => {
    if (!toolRegistry) return;
    const eb = eventBusRef.current;
    const bump = () => setToolPanelsVersion((version) => version + 1);
    const offRegistered = eb.on('tool:registered', bump);
    const offUnregistered = eb.on('tool:unregistered', bump);
    // Ensure we render once even if tools were registered before subscriptions
    bump();
    return () => {
      offRegistered?.();
      offUnregistered?.();
    };
  }, [toolRegistry]);

  // Get genogram state
  const nodes = useDiagramStore((state) => state.nodes);
  const edges = useDiagramStore((state) => state.edges);
  const frames = useDiagramStore((state) => state.frames);
  const selectedIds = useDiagramStore((state) => state.selectedIds);
  const storeZoom = useDiagramStore((state) => state.zoom);
  const setStage = useDiagramStore((state) => state.setStage);
  const templatesWithExampleData = useDiagramStore((state) => state.templatesWithExampleData);
  const stageScale = useDiagramStore((state) => state.stage.scale);

  // Get workflow state
  const selectedUseCase = useWorkflowStore(state => state.selectedUseCase);

  // Track template selection
  const [templateSelected, setTemplateSelected] = useState(false);
  // Track if we've loaded persisted workflow state to avoid showing screen prematurely
  const [workflowStateLoaded, setWorkflowStateLoaded] = useState(false);
  const [genoWizardDismissed, setGenoWizardDismissed] = useState(false);
  const [hierarchyChartWizardDismissed, setHierarchyChartWizardDismissed] = useState(false);
  const [flowchartAssistantPrefill, setFlowchartAssistantPrefill] = useState<{ token: number; text: string } | null>(null);
  // The process assistant consumes initialComposePrefill (not the
  // open-generate-mode event), so flowchart generate-from-text seeds a process
  // prompt into the compose box and expands the assistant.
  const handleFlowchartOpenGenerate = useCallback(() => {
    setFlowchartAssistantPrefill({
      token: Date.now(),
      text: `Describe your process step by step. For example:

A customer requests a refund. Support reviews the request. If under $50 it's auto-approved, otherwise it goes to a manager. Refund is issued.`,
    });
    if (typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(() => {
        try { window.dispatchEvent(new Event('assistant:expand')); } catch { /* noop */ }
      });
    } else {
      try { window.dispatchEvent(new Event('assistant:expand')); } catch { /* noop */ }
    }
  }, []);
  const flowchartPicker = useFlowchartStartPicker({
    commandManager: commandManagerRef.current,
    onSeeded: handleSeeded,
    onOpenGenerate: handleFlowchartOpenGenerate,
  });
  const orgPicker = useOrgChartStartPicker({
    commandManager: commandManagerRef.current,
    doc,
    requestFn: authenticatedFetch,
    folderId: currentFolderId,
    onSeeded: handleSeeded,
  });
  const didTrackGenoWizardAppLoadRef = useRef(false);
  const didTrackHierarchyChartWizardAppLoadRef = useRef(false);
  const didAutoApplyGenogramTemplateRef = useRef(false);

  // Reset template selection when use case changes
  useEffect(() => {
    if (!selectedUseCase) {
      setTemplateSelected(false);
    }
  }, [selectedUseCase]);

  const didAutoApplyUseCaseRef = useRef(false);
  useEffect(() => {
    if (!isSynced || !workflowStateLoaded || didAutoApplyUseCaseRef.current) return;
    if (selectedUseCase || nodes.length > 0 || edges.length > 0) return;
    if (!onboarding.useCase) return;

    const validUseCases: UseCaseType[] = ['medical-genetic', 'family-therapy', 'social-work', 'legal-estate', 'general-research', 'blank'];
    if (!validUseCases.includes(onboarding.useCase as UseCaseType)) return;

    didAutoApplyUseCaseRef.current = true;
    const cm = commandManagerRef.current;
    cm.execute(new SetUseCaseCommand(onboarding.useCase as UseCaseType));

    if (onboarding.startView === 'generate') {
      setTemplateSelected(true);
      requestAnimationFrame(() => {
        window.dispatchEvent(new CustomEvent('open-generate-mode'));
      });
    }
    if (onboarding.startView === 'blank' || onboarding.useCase === 'blank') {
      setTemplateSelected(true);
    }
  }, [isSynced, workflowStateLoaded, selectedUseCase, nodes.length, edges.length, onboarding.useCase, onboarding.startView]);

  // Determine which view to show
  // Only show pickers after document is synced and workflow state is loaded to avoid flickering
  // For blank diagrams, isInitialLoading might stay true if there are no nodes,
  // so we rely on isSynced to ensure the document is ready
  // For 'blank' use case, skip template picker and go directly to canvas
  // Skip onboarding entirely in test mode (?test=true) for e2e tests
  // Use case selection and template picker are genogram-specific features
  const isTestMode = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('test') === 'true';
  const isEmbedMode = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('embed') === '1';
  const assistantPanelKind = resolveAssistantPanelKind({
    isEmbedMode,
    showAssistant,
    isDiagramTypeResolved,
    diagramType,
  });
  const activeCanvasProjection = useMultiCanvasViewStore((state) => state.activeProjection);
  const canvasProjectionSplitRatio = useMultiCanvasViewStore((state) => state.splitRatio);
  const setCanvasProjectionSplitRatio = useMultiCanvasViewStore((state) => state.setSplitRatio);
  const openCanvasProjection = useMultiCanvasViewStore((state) => state.openProjection);
  const closeCanvasProjection = useMultiCanvasViewStore((state) => state.closeProjection);
  const setCanvasProjectionLayout = useMultiCanvasViewStore((state) => state.setLayout);
  // Auto-open is per diagram, not per App mount. A plain boolean latch meant
  // that closing the pane once made it unreachable until the diagram was
  // reopened, because the effect had no way to distinguish "already handled
  // this diagram" from "user dismissed it".
  const positionStaffingProjectionOpenedForRef = useRef<string | null>(null);
  const shouldShowPositionStaffingWorkbench = !featureFlagsLoading
    && isAtlasWorkloadPlanningEnabled()
    && !isEmbedMode
    && isPositionChart;
  const openPositionStaffingWorkbench = useCallback(() => {
    openCanvasProjection(POSITION_STAFFING_PROJECTION_KIND);
    setCanvasProjectionLayout('split_75_25');
  }, [openCanvasProjection, setCanvasProjectionLayout]);

  useEffect(() => {
    if (
      shouldShowPositionStaffingWorkbench
      && diagramId
      && positionStaffingProjectionOpenedForRef.current !== diagramId
    ) {
      openPositionStaffingWorkbench();
      positionStaffingProjectionOpenedForRef.current = diagramId;
      return;
    }
    if (!shouldShowPositionStaffingWorkbench && positionStaffingProjectionOpenedForRef.current) {
      if (useMultiCanvasViewStore.getState().activeProjection?.kind === POSITION_STAFFING_PROJECTION_KIND) {
        closeCanvasProjection();
      }
      positionStaffingProjectionOpenedForRef.current = null;
    }
  }, [
    closeCanvasProjection,
    diagramId,
    openPositionStaffingWorkbench,
    shouldShowPositionStaffingWorkbench,
  ]);
  useEffect(() => () => {
    if (useMultiCanvasViewStore.getState().activeProjection?.kind === POSITION_STAFFING_PROJECTION_KIND) {
      closeCanvasProjection();
    }
  }, [closeCanvasProjection]);
  const canvasProjectionSecondaryRatio = 1 - canvasProjectionSplitRatio;
  const canvasProjectionGridTemplateColumns = activeCanvasProjection
    ? `minmax(0, ${canvasProjectionSplitRatio}fr) 8px minmax(0, ${canvasProjectionSecondaryRatio}fr)`
    : 'minmax(0, 1fr)';
  const floatingPropertiesRightOffset = activeCanvasProjection
    ? `calc(${canvasProjectionSecondaryRatio * 100}% + ${Math.round(8 * canvasProjectionSplitRatio)}px)`
    : '0px';
  // Wizard policy (StartIntent): a seed/`wizard=skip` means intent was
  // captured upstream — never re-ask with the genogram two-step wizard.
  const skipWizardForIntent = shouldSkipWizard(onboarding.startIntent);
  const startSurfacePolicy = resolveStartSurfacePolicy({
    isDiagramTypeResolved,
    diagramType,
    isGenogramDiagram,
    isFlowchartDiagram,
    isTestMode,
    isEmbedMode,
    skipWizardForIntent,
    selectedUseCase,
    templateSelected,
    nodeCount: nodes.length,
    edgeCount: edges.length,
    isLoadingAccess,
    isSynced,
    workflowStateLoaded,
    genoWizardRequested: onboarding.genoWizard !== null,
    genoWizardDismissed,
    hierarchyChartWizardRequested: onboarding.hierarchyChartWizard !== null,
    hierarchyChartWizardDismissed,
    flowchartPickerChosen: flowchartPicker.chosen,
    orgPickerChosen: orgPicker.chosen,
    hasOrgOptions: Boolean(onboarding.orgOptions),
    loadOrgOptionsSampleData: Boolean(onboarding.loadOrgOptionsSampleData),
    isSourceBackedProjection: Boolean(currentDiagramInfo.data?.viewDefinition?.projection),
  });
  const {
    isOrg,
    showGenoWizardDialog,
    showHierarchyChartWizardDialog,
    effectiveShowUseCaseSelection,
    effectiveShowTemplatePicker,
    showFlowchartStartPicker,
    showOrgStartPicker,
  } = startSurfacePolicy;
  const prevUseCaseSelectionRef = useRef(effectiveShowUseCaseSelection);
  const prevTemplatePickerRef = useRef(effectiveShowTemplatePicker);
  const didOpenStartupShapePickerRef = useRef(false);

  useEffect(() => {
    if (
      didTrackGenoWizardAppLoadRef.current ||
      onboarding.genoWizard === null ||
      !isGenogramDiagram ||
      !isSynced ||
      !workflowStateLoaded ||
      isLoadingAccess ||
      isEmbedMode
    ) {
      return;
    }

    didTrackGenoWizardAppLoadRef.current = true;
    Tracker.track(GENOGRAM_WIZARD.APP_LOAD, {
      hasStarterContent: nodes.length > 0 || edges.length > 0,
      nodeCount: nodes.length,
      edgeCount: edges.length,
    });
  }, [
    edges.length,
    isEmbedMode,
    isGenogramDiagram,
    isLoadingAccess,
    isSynced,
    nodes.length,
    onboarding.genoWizard,
    workflowStateLoaded,
  ]);

  useEffect(() => {
    if (
      didTrackHierarchyChartWizardAppLoadRef.current ||
      onboarding.hierarchyChartWizard === null ||
      !isOrg ||
      !isSynced ||
      isLoadingAccess ||
      isEmbedMode
    ) {
      return;
    }

    didTrackHierarchyChartWizardAppLoadRef.current = true;
    Tracker.track(HIERARCHY_CHART_WIZARD.APP_LOAD);
  }, [
    isEmbedMode,
    isLoadingAccess,
    isOrg,
    isSynced,
    onboarding.hierarchyChartWizard,
  ]);

  // Preview iframe UX: default to pan mode so drag pans instead of marquee-selecting.
  useEffect(() => {
    if (!isEmbedMode) return;
    try { useUIStore.getState().setPanMode(true); } catch {}
    return () => {
      try { useUIStore.getState().setPanMode(false); } catch {}
    };
  }, [isEmbedMode]);

  // Loading overlay visibility: blocks interactions until realtime is ready and
  // initial projection has hydrated any existing shapes. For blank diagrams, rely
  // solely on isSynced to avoid showing the overlay indefinitely.
  const isInitialLoading = useDiagramStore(s => s.isInitialLoading);
  // Org charts assemble visibly: nodes hydrate, auto-collapse runs, tree layout
  // reflows, then the viewport fits. Revealing the canvas the moment
  // isInitialLoading clears shows all of that ("skunkworks"). Hold the overlay
  // for the org's first fit so the user only ever sees the finished, centered
  // overview. Hard-bounded so it can never hang.
  const orgViewportFitCompletionCount = useOrgChartViewStore((s) => s.viewportFitCompletionCount);
  const [orgFirstFitPending, setOrgFirstFitPending] = useState(false);
  const orgFirstFitSessionRef = useRef({
    diagramId,
    baselineCompletionCount: orgViewportFitCompletionCount,
    done: false,
  });
  if (orgFirstFitSessionRef.current.diagramId !== diagramId) {
    orgFirstFitSessionRef.current = {
      diagramId,
      baselineCompletionCount: orgViewportFitCompletionCount,
      done: false,
    };
  }
  useEffect(() => {
    setOrgFirstFitPending(false);
  }, [diagramId]);
  useEffect(() => {
    if (diagramType !== 'org') return;
    const session = orgFirstFitSessionRef.current;
    if (session.done) return;
    const hasContent = nodes.length > 0;
    const canvasReady = isSynced && !isLoadingAccess;
    if (!hasContent || !canvasReady) return;
    // Enter the hold as soon as the org canvas has content and is ready.
    setOrgFirstFitPending(true);
    // Completion is durable. A saved viewport can restore and clear the
    // pending fit before this parent observes the transient request; the old
    // >0 -> 0 edge detector then waited for the 2.5s safety timeout.
    if (orgViewportFitCompletedSince(
      session.baselineCompletionCount,
      orgViewportFitCompletionCount,
    )) {
      session.done = true;
      setOrgFirstFitPending(false);
    }
  }, [
    diagramType,
    nodes.length,
    isSynced,
    isLoadingAccess,
    orgViewportFitCompletionCount,
  ]);
  useEffect(() => {
    if (!orgFirstFitPending) return;
    // Safety: never let the reveal hang if a fit request is dropped.
    const timer = window.setTimeout(() => {
      orgFirstFitSessionRef.current.done = true;
      setOrgFirstFitPending(false);
    }, 2500);
    return () => window.clearTimeout(timer);
  }, [orgFirstFitPending]);
  const {
    showCanvasLoadingOverlay,
    projectionLoadFailed: orgProjectionLoadFailed,
  } = resolveCanvasLoadingPolicy({
    isLoadingAccess,
    isSynced,
    isInitialLoading,
    nodeCount: nodes.length,
    orgFirstFitPending,
    isProjectionChart: isPeopleChart || isPositionChart,
    isProjectionLoading: isPeopleChart ? peopleChart.isLoading : positionChart.isLoading,
    hasProjectionData: isPeopleChart ? peopleChart.hasProjectionData : positionChart.hasProjectionData,
    projectionError: isPeopleChart ? peopleChart.error : positionChart.error,
  });
  const orgProjectionError = isPeopleChart ? peopleChart.error : positionChart.error;

  // Collapse assistant when Use Case selection is visible
  useEffect(() => {
    if (effectiveShowUseCaseSelection) {
      try { window.dispatchEvent(new Event('assistant:collapse')); } catch {}
    }
  }, [effectiveShowUseCaseSelection]);

  // Expand the assistant when entering the canvas (not selection or template
  // picker). Exception: blank "shape library" starts land with the assistant
  // collapsed and the shape picker open so users can start building immediately:
  //   - org-options onboarding, and
  //   - a blank network/blueprint "Draw a network diagram" (start=shapes).
  // Collapsing the assistant for these is deliberate — if the assistant opens at
  // the same time it grabs focus, which makes the freshly-opened Radix shape
  // picker popover close itself (focus-outside). One owner per intent avoids the
  // open/close flicker.
  // Demo signup iframe blocks startup open until the user continues/closes it.
  const onboardingDialogBlocksChrome = useOnboardingDialogBlocksChrome();
  useEffect(() => {
    const onCanvas =
      !effectiveShowUseCaseSelection &&
      !effectiveShowTemplatePicker &&
      isSynced &&
      !isLoadingAccess &&
      !(isInitialLoading && nodes.length > 0);
    if (!onCanvas) return;

    const isBlankOrgOptionsCanvas =
      (onboarding.orgOptions || onboarding.startView === 'manual') &&
      !onboarding.loadOrgOptionsSampleData &&
      !onboarding.startIntent?.seed &&
      !onboarding.sourceTemplateId &&
      !onboarding.assistantPrompt &&
      isOrg &&
      !isEmbedMode;

    const isBlankNetworkShapesCanvas =
      diagramType === 'network' &&
      onboarding.networkStartPanel === 'shapes' &&
      nodes.length === 0 &&
      !onboarding.startIntent?.seed &&
      !onboarding.sourceTemplateId &&
      !onboarding.assistantPrompt &&
      !isEmbedMode;

    const isBlankProcessCanvas =
      diagramType === 'flowchart' &&
      onboarding.startIntent?.wizard === 'skip' &&
      nodes.length === 0 &&
      !onboarding.startIntent?.seed &&
      !onboarding.sourceTemplateId &&
      !onboarding.assistantPrompt &&
      !isEmbedMode;

    const isBlankGenogramCanvas =
      diagramType === 'genogram' &&
      (onboarding.startView === 'blank' || onboarding.genogramStartMode === 'blank-direct') &&
      nodes.length === 0 &&
      !onboarding.startIntent?.seed &&
      !onboarding.sourceTemplateId &&
      !onboarding.assistantPrompt &&
      !isEmbedMode;

    const shouldOpenShapePicker =
      isBlankOrgOptionsCanvas ||
      isBlankNetworkShapesCanvas ||
      isBlankProcessCanvas ||
      isBlankGenogramCanvas;
    if (!shouldOpenShapePicker) {
      try { window.dispatchEvent(new Event('assistant:expand')); } catch {}
      return;
    }

    try { window.dispatchEvent(new Event('assistant:collapse')); } catch {}

    if (onboardingDialogBlocksChrome) return;
    if (didOpenStartupShapePickerRef.current) return;
    return scheduleOpenShapePicker(() => {
      didOpenStartupShapePickerRef.current = true;
    });
  }, [
    effectiveShowUseCaseSelection,
    effectiveShowTemplatePicker,
    isSynced,
    isLoadingAccess,
    isInitialLoading,
    nodes.length,
    onboarding.orgOptions,
    onboarding.startView,
    onboarding.loadOrgOptionsSampleData,
    onboarding.startIntent?.seed,
    onboarding.sourceTemplateId,
    onboarding.assistantPrompt,
    onboarding.networkStartPanel,
    onboarding.genogramStartMode,
    onboarding.startIntent?.wizard,
    diagramType,
    isOrg,
    isEmbedMode,
    toolPanelsVersion,
    onboardingDialogBlocksChrome,
  ]);

  // Template selection handlers
  const handleSelectTemplate = useCallback(async (templateId: string, config?: TemplateConfig) => {
    const cm = commandManagerRef.current;
    const viewportCenter = { x: 400, y: 300 };
    let templateCommand: ICommand | CompositeCommand | undefined;

    if (templateId === 'simple') {
      templateCommand = createIndividualTemplate(viewportCenter.x, viewportCenter.y);
    } else if (templateId === 'couple') {
      templateCommand = createCoupleTemplate(viewportCenter.x, viewportCenter.y);
    } else if (templateId === 'nuclear') {
      templateCommand = createNuclearFamilyTemplate(viewportCenter.x, viewportCenter.y);
    } else if (templateId === 'blended') {
      templateCommand = createBlendedFamilyTemplate(viewportCenter.x, viewportCenter.y);
    } else if (templateId === 'household') {
      templateCommand = createHouseholdTemplate(viewportCenter.x, viewportCenter.y);
    } else if (templateId === 'conflict-map') {
      templateCommand = createConflictMapTemplate(viewportCenter.x, viewportCenter.y);
    } else if (templateId === 'fusion-map') {
      templateCommand = createFusionMapTemplate(viewportCenter.x, viewportCenter.y);
      // Automatically switch assistant to the cultural perspective for fusion scenarios
      usePerspectiveStore.getState().setPerspective('cultural');
    } else if (templateId === 'legacy-map') {
      templateCommand = createLegacyMapTemplate(viewportCenter.x, viewportCenter.y);
      // Legacy map focuses on multi-gen health patterns; switch assistant to health perspective
      usePerspectiveStore.getState().setPerspective('health');
    } else if (templateId === 'medical-history-map') {
      templateCommand = createMedicalHistoryMapTemplate(viewportCenter.x, viewportCenter.y);
      usePerspectiveStore.getState().setPerspective('health');
    } else if (templateId === 'genetic-disoder-map') {
      templateCommand = createGeneticDisorderMapTemplate(viewportCenter.x, viewportCenter.y);
    } else if (templateId === 'phychosocial-map') {
      templateCommand = createPsychosocialMapTemplate(viewportCenter.x, viewportCenter.y);
      usePerspectiveStore.getState().setPerspective('health');
    } else if (templateId === 'genetic-history-map') {
      templateCommand = createGeneticHistoryMapTemplate(viewportCenter.x, viewportCenter.y);
      usePerspectiveStore.getState().setPerspective('health');
    } else if (templateId === 'behavioral-pattern-map') {
      templateCommand = createBehavioralPatternMapTemplate(viewportCenter.x, viewportCenter.y);
      usePerspectiveStore.getState().setPerspective('cultural');
    } else if (templateId === 'heritage-roots-map') {
      templateCommand = createHeritageRootsMapTemplate(viewportCenter.x, viewportCenter.y);
      usePerspectiveStore.getState().setPerspective('cultural');
    } else if (templateId === '2-gen') {
      const { createFamilyTemplate } = await import('../domains/genogram/components/GenogramAssistantPanel');
      templateCommand = await createFamilyTemplate(1, viewportCenter.x, viewportCenter.y, true);
    } else if (templateId === '3-gen') {
      const { createFamilyTemplate } = await import('../domains/genogram/components/GenogramAssistantPanel');
      templateCommand = await createFamilyTemplate(2, viewportCenter.x, viewportCenter.y, true);
    } else if (templateId === 'extended') {
      templateCommand = createExtendedFamilyTemplate(2, viewportCenter.x, viewportCenter.y);
    } else if (templateId === 'custom' && config) {
      const { createFamilyTemplate } = await import('../domains/genogram/components/GenogramAssistantPanel');
      templateCommand = await createFamilyTemplate(config.generations - 1, viewportCenter.x, viewportCenter.y, true);
    }

    if (templateCommand) {
      // Only apply layout if it's a CompositeCommand (other templates use BatchCreateNodesCommand which already has positions)
      // Skip layout for simple couple templates - they already have correct positions
      if ('getCommands' in templateCommand && typeof templateCommand.getCommands === 'function') {
        const commands = templateCommand.getCommands();
        const nodeCommands = commands.filter(cmd => cmd instanceof CreateNodeCommand);
        const edgeCommands = commands.filter(cmd => cmd instanceof CreateEdgeCommand);
        // Skip layout for simple couples (2 nodes, 1 marriage edge) - they already have correct positions
        const isSimpleCouple = nodeCommands.length === 2 && 
          edgeCommands.length === 1 && 
          edgeCommands[0].getParams?.()?.type === 'marriage';
        if (!isSimpleCouple) {
          applyLayoutToTemplate(templateCommand as CompositeCommand, viewportCenter);
        }
      }
      await cm.execute(templateCommand);
      // Treat generated template as the new baseline, so undo stays disabled
      cm.clear();
      useDiagramStore.getState().markTemplateWithExampleData(templateId);
      setTemplateSelected(true);
    }
  }, []);

  useEffect(() => {
    if (didAutoApplyGenogramTemplateRef.current) return;
    if (!isGenogramDiagram || !isSynced || !workflowStateLoaded || isLoadingAccess || isEmbedMode) return;
    if (!onboarding.sourceTemplateId || onboarding.sourceTemplateId.startsWith('network-')) return;
    if (!selectedUseCase || selectedUseCase === 'blank') return;
    if (nodes.length > 0 || edges.length > 0) return;

    didAutoApplyGenogramTemplateRef.current = true;
    void handleSelectTemplate(onboarding.sourceTemplateId);
  }, [
    edges.length,
    handleSelectTemplate,
    isEmbedMode,
    isGenogramDiagram,
    isLoadingAccess,
    isSynced,
    nodes.length,
    onboarding.sourceTemplateId,
    selectedUseCase,
    workflowStateLoaded,
  ]);

  const handleGenerateFromText = useCallback(() => {
    setTemplateSelected(true);
    // Will open generate mode in assistant panel
    setTimeout(() => {
      const event = new CustomEvent('open-generate-mode');
      window.dispatchEvent(event);
    }, 100);
  }, []);

  const handleStartBlank = useCallback(() => {
    // Use case is already set via SetUseCaseCommand in UseCaseSelectionView
    // Just mark template as selected to hide the template picker
    setTemplateSelected(true);
  }, []);

  const handleGenoWizardUseCaseConfirm = useCallback(async (useCaseId: UseCaseType) => {
    Tracker.track(GENOGRAM_WIZARD.OPTION_SELECTED, {
      useCase: useCaseId,
    });

    const cm = commandManagerRef.current;
    const commands: ICommand[] = [];
    if (nodes.length > 0 || edges.length > 0) {
      commands.push(new ClearGenogramCanvasCommand(nodes, edges));
    }
    commands.push(new SetUseCaseCommand(useCaseId));
    await cm.execute(
      commands.length === 1
        ? commands[0]!
        : new CompositeCommand('Select Geno Wizard Use Case', commands),
    );
    setTemplateSelected(useCaseId === 'blank');
    if (useCaseId !== 'blank') {
      try { window.dispatchEvent(new Event('assistant:collapse')); } catch {}
    }
  }, [edges, nodes]);

  const handleGenoWizardOpenChange = useCallback((nextOpen: boolean) => {
    if (!nextOpen) {
      setGenoWizardDismissed(true);
    }
  }, []);

  const handleHierarchyChartWizardOpenChange = useCallback((nextOpen: boolean) => {
    if (!nextOpen) {
      setHierarchyChartWizardDismissed(true);
    }
  }, []);

  const handleHierarchyChartWizardChoice = useCallback((choice: HierarchyChartWizardChoice) => {
    Tracker.track(HIERARCHY_CHART_WIZARD.OPTION_SELECTED, {
      option: choice,
    });

    const templateIds: Partial<Record<HierarchyChartWizardChoice, string>> = {
      'org-chart': 'mou1wmfq-r8m9b98p3',
      'tree-diagram': 'mr0essd7-cguzlbsf1',
      'work-breakdown': 'mr0eb732-wz418g9n9',
    };
    const templateId = templateIds[choice];

    if (choice === 'blank') {
      if (nodes.length > 0 || edges.length > 0) {
        void commandManagerRef.current.execute(new ClearOrgCanvasCommand(nodes, edges));
        commandManagerRef.current.clear();
      }
      setHierarchyChartWizardDismissed(true);
      return;
    }

    if (!templateId || typeof window === 'undefined') {
      setHierarchyChartWizardDismissed(true);
      return;
    }

    const folderMatch = window.location.pathname.match(/^\/f\/([^/]+)\/d\//);
    const targetPath = folderMatch?.[1] ? `/f/${folderMatch[1]}/d/new` : '/d/new';
    const params = new URLSearchParams({
      type: 'org',
      templateId,
      wizard: 'skip',
    });
    window.location.assign(`${targetPath}?${params.toString()}`);
  }, [edges, nodes]);

  const handleBackToUseCaseSelection = useCallback(() => {
    const cm = commandManagerRef.current;
    cm.execute(new SetUseCaseCommand(null));
    setTemplateSelected(false);
  }, []);

  // Convert selected IDs to selected node set for the shape toolbar.
  const selectedPersons = nodes.filter((node) => {
    if (!selectedIds.has(node.id)) return false;
    return true;
  });
  const selectedTables = selectedPersons.filter((node) => node.type === 'table');
  const onlyTablesSelected = selectedPersons.length > 0 && selectedTables.length === selectedPersons.length;
  const selectedShapeKey = selectedPersons.map((node) => node.id).sort().join('|');
  const [renderedSelectionGeometry, setRenderedSelectionGeometry] = useState<{
    selectionKey: string;
    bounds: { x: number; y: number; width: number; height: number };
  } | null>(null);
  const handleRenderedSelectionBoundsChange = useCallback((selection: {
    selectionKey: string;
    bounds: { x: number; y: number; width: number; height: number };
  } | null) => {
    setRenderedSelectionGeometry((current) => {
      if (current === selection) return current;
      if (
        current && selection
        && current.selectionKey === selection.selectionKey
        && current.bounds.x === selection.bounds.x
        && current.bounds.y === selection.bounds.y
        && current.bounds.width === selection.bounds.width
        && current.bounds.height === selection.bounds.height
      ) {
        return current;
      }
      return selection;
    });
  }, []);
  const selectedEdges = useMemo(() => {
    return edges.filter(e => selectedIds.has(e.id));
  }, [edges, selectedIds]);

  const toolContext = useMemo<IToolContext>(() => {
    const base = toolRegistry.getContext();

    const selectedNodes = selectedPersons;
    const contextTypes = new Set<string>();
    const countsByType: Record<string, number> = {};

    const getNodeContextType = (node: any): string => {
      const attrs = ((node as any)?.attrs ?? {}) as Record<string, unknown>;
      const category = typeof attrs.category === 'string' ? attrs.category : undefined;
      if (node?.type === 'text') return 'text';
      if (node?.type === 'sticky-note' || category === 'sticky-note') return 'sticky-note';
      if (node?.type === 'image') return 'image';
      if (node?.type === 'frame') return 'frame';
      if (node?.type === 'person') return 'person';
      return 'genericShape';
    };

    selectedNodes.forEach((node) => {
      const type = getNodeContextType(node);
      contextTypes.add(type);
      countsByType[type] = (countsByType[type] || 0) + 1;
    });
    if (selectedEdges.length > 0) {
      countsByType.connector = (countsByType.connector || 0) + selectedEdges.length;
      if (selectedNodes.length === 0) contextTypes.add('connector');
    }

    const contextType = contextTypes.size === 1
      ? Array.from(contextTypes)[0]
      : contextTypes.size > 1
        ? 'mixed'
        : 'none';

    const selectionSummary = {
      contextType: contextType as any,
      typesSet: contextTypes as any,
      countsByType,
      diagramType: base.diagramType,
      flags: {
        hasText: selectedNodes.some((node) => node?.text || node?.name || node?.type === 'text' || node?.type === 'sticky-note'),
        hasConnectors: selectedEdges.length > 0,
      },
      version: 1,
    };

    let selectionStyles = base.selectionStyles;
    if (selectedNodes.length > 0) {
      const firstNode: any = selectedNodes[0];
      selectionStyles = {
        fillColor: firstNode?.fillColor || firstNode?.attrs?.fillColor,
        strokeColor: firstNode?.strokeColor || firstNode?.attrs?.strokeColor,
        strokeWidth: firstNode?.attrs?.strokeWidth || 2,
        fontSize: firstNode?.attrs?.fontSize || firstNode?.fontSize,
        fontFamily: firstNode?.attrs?.fontFamily || firstNode?.fontFamily,
        textColor: firstNode?.attrs?.textColor,
        textAlign: firstNode?.attrs?.textAlign,
        verticalAlign: firstNode?.attrs?.verticalAlign,
        dash: firstNode?.attrs?.dash,
        dashEnabled: firstNode?.attrs?.dashEnabled,
        isMixed: {},
      } as any;
    }

    return {
      ...base,
      // The registry is constructed before diagram access resolves and starts
      // fail-closed. Always project the live access result back into the tool
      // context so domain property tools become available (or revoke
      // immediately) with the editor's actual write permission.
      canWrite,
      selection: selectedIds,
      selectionSummary: selectionSummary as any,
      selectionStyles: selectionStyles as any,
      getSelectedShapes: () =>
        Array.from(selectedIds)
          .map((id) => base.shapes.get(id))
          .filter(Boolean) as any,
      getSelectedConnectors: () =>
        selectedEdges.map((edge: any) => ({
          id: edge.id,
          type: edge.type,
          from: edge.from,
          to: edge.to,
          data: edge,
          render: () => null,
          update: () => { },
          destroy: () => { },
        })) as any,
    };
  }, [canWrite, toolRegistry, selectedIds, selectedPersons, selectedEdges]);

  const lastToolSelectionSignatureRef = useRef<string>('');
  useEffect(() => {
    if (!toolRegistry) return;
    const eventBus = eventBusRef.current;
    const getSelectionSignature = () => {
      const selection = toolRegistry.getContext().selection;
      if (!selection || selection.size === 0) return '';
      return Array.from(selection).sort().join('|');
    };
    lastToolSelectionSignatureRef.current = getSelectionSignature();
    const handleContextChanged = () => {
      const nextSignature = getSelectionSignature();
      if (nextSignature === lastToolSelectionSignatureRef.current) return;
      lastToolSelectionSignatureRef.current = nextSignature;
    };
    const unsubscribeContextChanged = eventBus.on('tool:contextChanged', handleContextChanged);
    return () => {
      unsubscribeContextChanged?.();
    };
  }, [toolRegistry]);

  // Initialize mutation manager on mount
  useEffect(() => {
    getNodeMutationManager();
  }, []);

  // Access control: gate editing based on context permissions
  const canWriteRef = useRef<boolean>(canWrite);
  useEffect(() => { canWriteRef.current = canWrite; }, [canWrite]);

  // Listen for command events to update undo/redo state and gate execution
  useEffect(() => {
    const eventBus = eventBusRef.current;
    
    // Bridge tool-emitted commands to the command manager (e.g., FramesTool emits 'command:execute')
    const unsubscribeToolExec = eventBus.on('command:execute', (command: ICommand) => {
      if (!canWriteRef.current) {
        appLogger.warn('Edit blocked: read-only access');
        return;
      }
      try { commandManagerRef.current.execute(command); } catch (err) { appLogger.error('Failed to execute command from event bus:', err); }
    });

    const unsubscribeExecuted = eventBus.on('command:executed', ({ canUndo, canRedo }: { canUndo: boolean; canRedo: boolean }) => {
      setCanUndo(canUndo);
      setCanRedo(canRedo);
    });
    
    const unsubscribeUndone = eventBus.on('command:undone', ({ canUndo, canRedo }: { canUndo: boolean; canRedo: boolean }) => {
      setCanUndo(canUndo);
      setCanRedo(canRedo);
    });
    
    const unsubscribeRedone = eventBus.on('command:redone', ({ canUndo, canRedo }: { canUndo: boolean; canRedo: boolean }) => {
      setCanUndo(canUndo);
      setCanRedo(canRedo);
    });
    
    const unsubscribeCleared = eventBus.on('command:cleared', ({ canUndo, canRedo }: { canUndo: boolean; canRedo: boolean }) => {
      setCanUndo(canUndo);
      setCanRedo(canRedo);
    });

    return () => {
      unsubscribeToolExec();
      unsubscribeExecuted();
      unsubscribeUndone();
      unsubscribeRedone();
      unsubscribeCleared();
    };
  }, []);

  useEffect(() => {
    if (prevUseCaseSelectionRef.current && !effectiveShowUseCaseSelection) {
      commandManagerRef.current.clear();
    }
    prevUseCaseSelectionRef.current = effectiveShowUseCaseSelection;
  }, [effectiveShowUseCaseSelection]);

  useEffect(() => {
    if (prevTemplatePickerRef.current && !effectiveShowTemplatePicker) {
      commandManagerRef.current.clear();
    }
    prevTemplatePickerRef.current = effectiveShowTemplatePicker;
  }, [effectiveShowTemplatePicker]);

  // Start genogram effect engine (materialize inherited culture) - only for genogram diagrams
  useEffect(() => {
    if (diagramType !== 'genogram') return;
    const eb = eventBusRef.current;
    const engine = new GenogramEffectEngine(eb);
    engine.start();
    return () => engine.dispose();
  }, [diagramType]);

  useEffect(() => {
    const eventBus = eventBusRef.current;
    // Guard against duplicate emissions within the same tick (e.g., double key handlers)
    const undoGuardRef = { pending: false } as { pending: boolean };
    const redoGuardRef = { pending: false } as { pending: boolean };
    const handleUndoRequest = async () => {
      if (undoGuardRef.pending) return;
      undoGuardRef.pending = true;
      try {
        await commandManagerRef.current.undo();
      } catch (err) {
        appLogger.error('Undo failed', err);
      } finally {
        // Reset on next tick to allow subsequent user-initiated undos
        setTimeout(() => { undoGuardRef.pending = false; }, 0);
      }
    };
    const handleRedoRequest = async () => {
      if (redoGuardRef.pending) return;
      redoGuardRef.pending = true;
      try {
        await commandManagerRef.current.redo();
      } catch (err) {
        appLogger.error('Redo failed', err);
      } finally {
        setTimeout(() => { redoGuardRef.pending = false; }, 0);
      }
    };
    const unsubscribeUndoRequest = eventBus.on('command:request:undo', handleUndoRequest);
    const unsubscribeRedoRequest = eventBus.on('command:request:redo', handleRedoRequest);
    return () => {
      unsubscribeUndoRequest();
      unsubscribeRedoRequest();
    };
  }, []);

  // Command handlers
  const handleUndo = useCallback(() => {
    eventBusRef.current.emit('command:request:undo');
  }, []);
  const handleRedo = useCallback(() => {
    eventBusRef.current.emit('command:request:redo');
  }, []);
  
  // Zoom handlers
  const handleZoomIn = () => storeZoom(0.1, 0, 0);
  const handleZoomOut = () => storeZoom(-0.1, 0, 0);
  const applyZoomAtCenter = useCallback((targetScale: number) => {
    const stageInstance = stageRef.current;
    if (!stageInstance) return;
    const containerWidth = stageInstance.width();
    const containerHeight = stageInstance.height();
    if (containerWidth === 0 || containerHeight === 0) return;

    const pointer = { x: containerWidth / 2, y: containerHeight / 2 };
    const currentScale = stageInstance.scaleX();
    const stagePosition = stageInstance.position();

    const worldPos = {
      x: (pointer.x - stagePosition.x) / currentScale,
      y: (pointer.y - stagePosition.y) / currentScale,
    };

    const clampedScale = Math.min(2, Math.max(0.05, targetScale));
    const newPos = {
      x: pointer.x - worldPos.x * clampedScale,
      y: pointer.y - worldPos.y * clampedScale,
    };

    stageInstance.scale({ x: clampedScale, y: clampedScale });
    stageInstance.position(newPos);
    stageInstance.batchDraw();
    setStage({ scale: clampedScale, offsetX: newPos.x, offsetY: newPos.y });
  }, [setStage]);

  const handleZoomToScale = useCallback((targetScale: number) => {
    applyZoomAtCenter(targetScale);
  }, [applyZoomAtCenter]);

  const handleZoomResetView = useCallback(() => {
    const stageInstance = stageRef.current;
    if (!stageInstance) return;
    stageInstance.scale({ x: 1, y: 1 });
    stageInstance.position({ x: 0, y: 0 });
    stageInstance.batchDraw();
    setStage({ scale: 1, offsetX: 0, offsetY: 0 });
  }, [setStage]);

  const handleZoomToFit = useCallback(() => {
    // Org charts must fit against rendered/collapsed-visible nodes (handled in canvas),
    // not raw store nodes, otherwise large hidden trees can force extreme zoom-out.
    if (diagramType === 'org') {
      // Forced: this is a user asking to re-frame, so the canvas must recompute
      // the fit rather than restore the persisted viewport (which is simply
      // where the user already is, making the request a no-op).
      useOrgChartViewStore.getState().requestViewportFit({ force: true });
      return;
    }

    const stageInstance = stageRef.current;
    if (!stageInstance) return;
    if (nodes.length === 0 && edges.length === 0 && frames.length === 0) {
      handleZoomResetView();
      return;
    }

    const applyFit = (): boolean => {
      const containerWidth = stageInstance.width();
      const containerHeight = stageInstance.height();
      if (containerWidth === 0 || containerHeight === 0) return false;

      const fit = computeDiagramFitToContent(
        {
          nodes,
          edges,
          frames,
          labelGeometry: {
            line2Field: useLabelSettingsStore.getState().line2Field,
          },
        },
        { width: containerWidth, height: containerHeight },
        { padding: 80 },
      );

      if (!fit) {
        handleZoomResetView();
        return true;
      }

      const currentScale = stageInstance.scaleX();
      if (Math.abs(fit.scale - currentScale) > 1e-6) {
        stageInstance.scale({ x: fit.scale, y: fit.scale });
      }
      stageInstance.position({ x: fit.offsetX, y: fit.offsetY });
      stageInstance.batchDraw();
      setStage(fit);
      return true;
    };

    if (applyFit()) return;

    const startedAt = Date.now();
    const MAX_WAIT_MS = 1500;
    const RETRY_INTERVAL_MS = 50;
    const retry = () => {
      if (Date.now() - startedAt > MAX_WAIT_MS) {
        handleZoomResetView();
        return;
      }
      window.setTimeout(() => {
        window.requestAnimationFrame(() => {
          if (applyFit()) return;
          retry();
        });
      }, RETRY_INTERVAL_MS);
    };
    retry();
  }, [diagramType, nodes, edges, frames, handleZoomResetView, setStage]);

  // Perform an initial zoom-to-fit after a template is inserted and canvas is shown
  const initialFitDoneRef = useRef(false);
  const lastStableContentKeyRef = useRef<string | null>(null);
  const fitTimerRef = useRef<number | null>(null);

  useEffect(() => {
    // Reset the flag and any pending timers when leaving canvas or when nodes are cleared
    if (effectiveShowUseCaseSelection || effectiveShowTemplatePicker || (nodes.length === 0 && edges.length === 0)) {
      initialFitDoneRef.current = false;
      lastStableContentKeyRef.current = null;
      if (fitTimerRef.current !== null) {
        clearTimeout(fitTimerRef.current);
        fitTimerRef.current = null;
      }
    }
  }, [effectiveShowUseCaseSelection, effectiveShowTemplatePicker, nodes.length, edges.length]);

  useEffect(() => {
    const onCanvas = !effectiveShowUseCaseSelection && !effectiveShowTemplatePicker && isSynced && !isLoadingAccess;
    if (!onCanvas) return;
    if (nodes.length === 0 && edges.length === 0) return;
    if (initialFitDoneRef.current) return;

    // Auto-fit when:
    // 1. A template was inserted (hasTemplateExample), OR
    // 2. Initial loading just completed with shapes and/or connectors (imported diagrams, loaded diagrams)
    const hasTemplateExample = templatesWithExampleData && templatesWithExampleData.size > 0;
    const shouldFitForInitialLoad =
      !isInitialLoading && (nodes.length > 0 || edges.length > 0) && !hasTemplateExample;

    const shouldFitForEmbed = isEmbedMode && !isInitialLoading && (nodes.length > 0 || edges.length > 0);
    if (!hasTemplateExample && !shouldFitForInitialLoad && !shouldFitForEmbed) return;

    const stableKey = `${nodes.length}:${edges.length}`;
    if (lastStableContentKeyRef.current !== stableKey) {
      lastStableContentKeyRef.current = stableKey;
      if (fitTimerRef.current !== null) {
        clearTimeout(fitTimerRef.current);
      }
      fitTimerRef.current = window.setTimeout(() => {
        // Org chart uses a domain-specific fitter that fits against renderedNodes
        // (collapsed/projected) and waits for stage + projection to be ready.
        if (diagramType === 'org') {
          useOrgChartViewStore.getState().requestViewportFit();
          initialFitDoneRef.current = true;
          return;
        }
        if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
          window.requestAnimationFrame(() => {
            window.requestAnimationFrame(() => {
              try { handleZoomToFit(); } catch {}
              initialFitDoneRef.current = true;
            });
          });
        } else {
          try { handleZoomToFit(); } catch {}
          initialFitDoneRef.current = true;
        }
      }, 150);
    }
  }, [effectiveShowUseCaseSelection, effectiveShowTemplatePicker, isSynced, isLoadingAccess, nodes.length, edges.length, templatesWithExampleData, isInitialLoading, handleZoomToFit, isEmbedMode, diagramType]);

  // DL-2.5: network design tools (apply_pattern, compose_service)
  // dispatch a `network:zoom-to-fit` window event after their
  // CompositeCommand executes so the user sees the freshly-applied
  // resources. We listen here and route to the canonical
  // handleZoomToFit. Scoped to network diagrams.
  useEffect(() => {
    if (diagramType !== 'network') return;
    const onFit = () => {
      try { handleZoomToFit(); } catch { /* fit is best-effort */ }
    };
    window.addEventListener('network:zoom-to-fit', onFit);
    return () => window.removeEventListener('network:zoom-to-fit', onFit);
  }, [diagramType, handleZoomToFit]);

  // Keep the seed-fit ref pointed at the live fit closure. Seed paths
  // (useStartIntentSeed + the flowchart picker) call handleSeeded → this ref
  // after hydrating content, so the user lands framed on the seed rather than
  // scrolled off to the layout origin. Two RAFs let the Konva stage render
  // the new nodes before we measure.
  useEffect(() => {
    fitAfterSeedRef.current = () => {
      const fit = () => {
        if (diagramType === 'org') {
          try { useOrgChartViewStore.getState().requestViewportFit(); } catch { /* best-effort */ }
          return;
        }
        try { handleZoomToFit(); } catch { /* best-effort */ }
      };
      if (typeof window.requestAnimationFrame === 'function') {
        window.requestAnimationFrame(() => window.requestAnimationFrame(() => window.setTimeout(fit, 80)));
      } else {
        fit();
      }
    };
  }, [diagramType, handleZoomToFit]);



  // Calculate selection bounds for contextual toolbar
  // Compute robust selection bounds (freehand-aware) for the floating toolbar
  const selectionBounds = selectedPersons.length > 0 ? (() => {
    // For freehand nodes, derive bounds from polyline points (+ node offset)
    const boundsForNode = (n: any) => {
      if (n?.type === 'freehand') {
        const pts: number[] = Array.isArray(n?.attrs?.points) ? n.attrs.points as number[] : [];
        const offX = typeof n?.x === 'number' ? n.x : 0;
        const offY = typeof n?.y === 'number' ? n.y : 0;
        if (pts.length >= 2) {
          let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
          for (let i = 0; i < pts.length; i += 2) {
            const px = (pts[i] ?? 0) + offX;
            const py = (pts[i + 1] ?? 0) + offY;
            if (px < minX) minX = px;
            if (py < minY) minY = py;
            if (px > maxX) maxX = px;
            if (py > maxY) maxY = py;
          }
          // Provide a minimum footprint to ensure the toolbar has space to render
          if (!isFinite(minX) || !isFinite(minY) || !isFinite(maxX) || !isFinite(maxY)) {
            return { x: offX, y: offY, width: 0, height: 0 };
          }
          return { x: minX, y: minY, width: Math.max(0, maxX - minX), height: Math.max(0, maxY - minY) };
        }
        return { x: offX, y: offY, width: 0, height: 0 };
      }
      // Generic nodes – use x/y and known width/height fallbacks
      const nx = typeof n?.x === 'number' ? n.x : 0;
      const ny = typeof n?.y === 'number' ? n.y : 0;
      const nw = (typeof (n?.w ?? n?.width) === 'number' ? (n.w ?? n.width) : 0) as number;
      const nh = (typeof (n?.h ?? n?.height) === 'number' ? (n.h ?? n.height) : 0) as number;
      if (n?.type === 'org_person_photo') {
        return getOrgPersonPhotoTopCenterVisualBounds(nx, ny, nw, nh);
      }
      return { x: nx, y: ny, width: Math.max(0, nw), height: Math.max(0, nh) };
    };

    const nodeBounds = selectedPersons.map(boundsForNode);
    const minX = Math.min(...nodeBounds.map(b => b.x));
    const minY = Math.min(...nodeBounds.map(b => b.y));
    const maxX = Math.max(...nodeBounds.map(b => b.x + b.width));
    const maxY = Math.max(...nodeBounds.map(b => b.y + b.height));

    // Add a small padding so the toolbar sits comfortably above
    const pad = 50;
    return {
      x: minX - pad,
      y: minY - pad,
      width: Math.max(0, (maxX - minX) + pad * 2),
      height: Math.max(0, (maxY - minY) + pad * 2)
    };
  })() : { x: 0, y: 0, width: 0, height: 0 };
  const hasRenderedOrgSelectionBounds = diagramType !== 'org'
    || renderedSelectionGeometry?.selectionKey === selectedShapeKey;
  const toolbarSelectionBounds = diagramType === 'org' && renderedSelectionGeometry
    ? renderedSelectionGeometry.bounds
    : selectionBounds;

  const edgeBounds = useMemo(() => {
    if (selectedEdges.length === 0) return { x: 0, y: 0, width: 0, height: 0 };
    const { nodes } = useDiagramStore.getState();
    const edge = selectedEdges[0] as any;
    const from = nodes.find(n => n.id === edge.from);
    const to = nodes.find(n => n.id === edge.to);

    // If this is a generic connector that uses a free endpoint (no second shape),
    // compute bounds around the actual connector span so the toolbar can sit
    // outside the path instead of landing on its midpoint.
    // Supported free endpoint fields (written by EdgeConnector): attrs.fromPoint / attrs.toPoint
    if (!from || !to) {
      const attrs = (edge as any)?.attrs || {};
      const freeFrom: { x: number; y: number } | undefined = attrs.fromPoint;
      const freeTo: { x: number; y: number } | undefined = attrs.toPoint;

      const boundsOf = (n: any) => {
        const w = (n?.w ?? n?.width ?? 0) as number;
        const h = (n?.h ?? n?.height ?? 0) as number;
        return { x: (n?.x ?? 0) as number, y: (n?.y ?? 0) as number, w, h };
      };

      const inferSideToward = (
        b: { x: number; y: number; w: number; h: number },
        target: { x: number; y: number } | null,
      ): 'top' | 'right' | 'bottom' | 'left' => {
        if (!target) return 'right';
        const cx = b.x + b.w / 2;
        const cy = b.y + b.h / 2;
        const dx = target.x - cx;
        const dy = target.y - cy;
        if (Math.abs(dx) >= Math.abs(dy)) return dx >= 0 ? 'right' : 'left';
        return dy >= 0 ? 'bottom' : 'top';
      };

      const pickPoint = (
        n: any,
        explicitSide: unknown,
        offset: unknown,
        target: { x: number; y: number } | null,
      ) => {
        const b = boundsOf(n);
        const side =
          explicitSide === 'top' || explicitSide === 'right' || explicitSide === 'bottom' || explicitSide === 'left'
            ? explicitSide
            : inferSideToward(b, target);
        const t = typeof offset === 'number' ? Math.max(0, Math.min(1, offset)) : 0.5;
        switch (side) {
          case 'top': return { x: b.x + b.w * t, y: b.y };
          case 'right': return { x: b.x + b.w, y: b.y + b.h * t };
          case 'bottom': return { x: b.x + b.w * t, y: b.y + b.h };
          case 'left': return { x: b.x, y: b.y + b.h * t };
        }
      };

      const p0 = from ? pickPoint(from, (edge as any).fromAnchor, (edge as any).fromOffset, freeTo || null) : (freeFrom || null);
      const p1 = to ? pickPoint(to, (edge as any).toAnchor, (edge as any).toOffset, freeFrom || null) : (freeTo || null);

      if (p0 && p1) {
        const finiteFlatPoints = (value: unknown): number[] | null => {
          if (!Array.isArray(value) || value.length < 4 || value.length % 2 !== 0) return null;
          const points = value.map(Number);
          return points.every(Number.isFinite) ? points : null;
        };
        const attrPoints =
          finiteFlatPoints(attrs.layoutPoints) ||
          finiteFlatPoints(attrs.points) ||
          finiteFlatPoints((edge as any).points);
        const edgeRoutingType = typeof attrs.routingType === 'string' ? attrs.routingType : undefined;
        const routePoints = (() => {
          if (attrPoints) return attrPoints;
          if (
            edgeRoutingType === 'orthogonal' ||
            edgeRoutingType === 'orthogonalRounded' ||
            edgeRoutingType === 'rounded'
          ) {
            const sourceSide =
              (edge as any).fromAnchor === 'top' ||
              (edge as any).fromAnchor === 'right' ||
              (edge as any).fromAnchor === 'bottom' ||
              (edge as any).fromAnchor === 'left'
                ? (edge as any).fromAnchor
                : inferSideToward(
                    { x: p0.x, y: p0.y, w: 0, h: 0 },
                    p1,
                  );
            const targetSide =
              (edge as any).toAnchor === 'top' ||
              (edge as any).toAnchor === 'right' ||
              (edge as any).toAnchor === 'bottom' ||
              (edge as any).toAnchor === 'left'
                ? (edge as any).toAnchor
                : inferSideToward(
                    { x: p1.x, y: p1.y, w: 0, h: 0 },
                    p0,
                  );
            const sideVector = (side: 'top' | 'right' | 'bottom' | 'left', inward: boolean) => {
              switch (side) {
                case 'top': return inward ? { x: 0, y: 1 } : { x: 0, y: -1 };
                case 'right': return inward ? { x: -1, y: 0 } : { x: 1, y: 0 };
                case 'bottom': return inward ? { x: 0, y: -1 } : { x: 0, y: 1 };
                case 'left': return inward ? { x: 1, y: 0 } : { x: -1, y: 0 };
              }
            };
            const gap = 12;
            const vFrom = sideVector(sourceSide, false);
            const vTo = sideVector(targetSide, true);
            const pA = { x: p0.x + vFrom.x * gap, y: p0.y + vFrom.y * gap };
            const pB = { x: p1.x - vTo.x * gap, y: p1.y - vTo.y * gap };
            const fromAxis = sourceSide === 'left' || sourceSide === 'right' ? 'horizontal' : 'vertical';
            const toAxis = targetSide === 'left' || targetSide === 'right' ? 'horizontal' : 'vertical';
            const points = [p0.x, p0.y, pA.x, pA.y];
            if (fromAxis !== toAxis) {
              if (fromAxis === 'horizontal') {
                points.push(pB.x, pA.y);
              } else {
                points.push(pA.x, pB.y);
              }
              points.push(pB.x, pB.y);
            } else if (fromAxis === 'horizontal') {
              const midX = (pA.x + pB.x) / 2;
              points.push(midX, pA.y, midX, pB.y, pB.x, pB.y);
            } else {
              const midY = (pA.y + pB.y) / 2;
              points.push(pA.x, midY, pB.x, midY, pB.x, pB.y);
            }
            points.push(p1.x, p1.y);
            return points;
          }
          return [p0.x, p0.y, p1.x, p1.y];
        })();
        const xs = routePoints.filter((_, index) => index % 2 === 0);
        const ys = routePoints.filter((_, index) => index % 2 === 1);
        const minX = Math.min(...xs);
        const minY = Math.min(...ys);
        const maxX = Math.max(...xs);
        const maxY = Math.max(...ys);
        return { x: minX, y: minY, width: Math.max(1, maxX - minX), height: Math.max(1, maxY - minY) };
      }
      // If only one point is known, fall back to a tiny box around it
      const single = p0 || p1;
      if (single) {
        return { x: single.x - 24, y: single.y - 12, width: 48, height: 24 };
      }
      // No usable endpoints – keep previous fallback
      return { x: 0, y: 0, width: 0, height: 0 };
    }

    // Helper: bounds for a node (freehand-aware)
    const boundsForNode = (n: any) => {
      if (n?.type === 'freehand') {
        const pts: number[] = Array.isArray(n?.attrs?.points) ? (n.attrs.points as number[]) : [];
        const offX = Number(n?.x) || 0;
        const offY = Number(n?.y) || 0;
        if (pts.length >= 2) {
          let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
          for (let i = 0; i < pts.length; i += 2) {
            const px = (pts[i] ?? 0) + offX;
            const py = (pts[i + 1] ?? 0) + offY;
            if (px < minX) minX = px;
            if (py < minY) minY = py;
            if (px > maxX) maxX = px;
            if (py > maxY) maxY = py;
          }
          return { x: minX, y: minY, w: Math.max(0, maxX - minX), h: Math.max(0, maxY - minY) };
        }
        return { x: offX, y: offY, w: 0, h: 0 };
      }
      const w = (n.w ?? n.width ?? 0) as number; const h = (n.h ?? n.height ?? 0) as number;
      return { x: n.x, y: n.y, w, h };
    };

    // Marriage connector: place toolbar near the marriage bar baseline
    if (edge.type === 'marriage') {
      const bFrom = boundsForNode(from);
      const bTo = boundsForNode(to);
      // Label-aware pad for person nodes (matches connector code)
      const computeLabelPad = (n: any): number => {
        if (n?.type !== 'person') return 0;
        const fs = ((n?.attrs?.fontSize ?? n?.fontSize ?? 12) as number);
        const base = 5 + Math.max(16, fs + 6);
        const lines = String(n?.name || '').split(/\r?\n/).length;
        const extra = Math.max(0, lines - 1) * Math.round(fs * 1.1);
        return base + extra;
      };
      const fromBottom = bFrom.y + bFrom.h + computeLabelPad(from);
      const toBottom = bTo.y + bTo.h + computeLabelPad(to);
      const centerFromX = bFrom.x + bFrom.w / 2;
      const centerToX = bTo.x + bTo.w / 2;
      const baselineY = Math.round(Math.max(fromBottom, toBottom) + 20);
      const minX = Math.min(centerFromX, centerToX);
      const maxX = Math.max(centerFromX, centerToX);
      // A tight band around the baseline so the toolbar anchors close to the line
      return { x: minX, y: baselineY - 8, width: Math.max(0, maxX - minX), height: 16 };
    }

    // Freehand-aware placement: compute a band around the actual path midpoint
    if (from.type === 'freehand' || to.type === 'freehand') {
      const routingType = ((edge.attrs as any)?.routingType as 'straight'|'orthogonal'|'bezier'|undefined) || 'bezier';
      const pickPoint = (n: any, side?: 'top'|'right'|'bottom'|'left', offset = 0.5) => {
        let x = n.x, y = n.y;
        let w = (n.w ?? n.width ?? 60) as number;
        let h = (n.h ?? n.height ?? 60) as number;
        if (n?.type === 'freehand') {
          const pts: number[] = Array.isArray(n?.attrs?.points) ? (n.attrs.points as number[]) : [];
          if (pts.length >= 4) {
            let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
            for (let i = 0; i < pts.length; i += 2) {
              const px = pts[i] as number; const py = pts[i + 1] as number;
              if (!Number.isFinite(px) || !Number.isFinite(py)) continue;
              if (px < minX) minX = px; if (py < minY) minY = py;
              if (px > maxX) maxX = px; if (py > maxY) maxY = py;
            }
            if (Number.isFinite(minX) && Number.isFinite(minY) && Number.isFinite(maxX) && Number.isFinite(maxY)) {
              x = n.x + minX; y = n.y + minY; w = Math.max(1, maxX - minX); h = Math.max(1, maxY - minY);
            }
          }
        }
        const t = Math.max(0, Math.min(1, offset));
        switch (side) {
          case 'top': return { x: x + w * t, y };
          case 'right': return { x: x + w, y: y + h * t };
          case 'bottom': return { x: x + w * t, y: y + h };
          case 'left': return { x, y: y + h * t };
          default: return { x: x + w / 2, y: y + h / 2 };
        }
      };
      const fromAnchor = ((edge as any).fromAnchor as any) || 'bottom';
      const toAnchor = ((edge as any).toAnchor as any) || 'top';
      const p0 = pickPoint(from, fromAnchor, (edge as any).fromOffset ?? 0.5);
      const p3 = pickPoint(to, toAnchor, (edge as any).toOffset ?? 0.5);

      if (routingType !== 'bezier') {
        const mx = (p0.x + p3.x) / 2; const my = (p0.y + p3.y) / 2;
        return { x: mx - 40, y: my - 12, width: 80, height: 24 };
      }
      const sideVector = (side: 'top'|'right'|'bottom'|'left', inward: boolean) => {
        switch (side) {
          case 'top': return inward ? { x: 0, y: 1 } : { x: 0, y: -1 };
          case 'right': return inward ? { x: -1, y: 0 } : { x: 1, y: 0 };
          case 'bottom': return inward ? { x: 0, y: -1 } : { x: 0, y: 1 };
          case 'left': return inward ? { x: 1, y: 0 } : { x: -1, y: 0 };
        }
      };
      const rotateVec = (vx: number, vy: number, deg: number) => {
        const r = (deg || 0) * Math.PI / 180; const cos = Math.cos(r), sin = Math.sin(r);
        return { x: vx * cos - vy * sin, y: vx * sin + vy * cos };
      };
      const fromRot = (((from as any).attrs)?.rotation as number) || 0;
      const toRot = (((to as any).attrs)?.rotation as number) || 0;
      const baseLen = Math.min(160, Math.max(40, Math.hypot(p3.x - p0.x, p3.y - p0.y) * 0.35));
      const vFrom = rotateVec(sideVector(fromAnchor, false)!.x, sideVector(fromAnchor, false)!.y, fromRot);
      const vTo = rotateVec(sideVector(toAnchor, true)!.x, sideVector(toAnchor, true)!.y, toRot);
      const c1 = { x: p0.x + vFrom.x * baseLen, y: p0.y + vFrom.y * baseLen };
      const c2 = { x: p3.x - vTo.x * baseLen, y: p3.y - vTo.y * baseLen };
      const t = 0.5; const it = 1 - t;
      const midX = it*it*it*p0.x + 3*it*it*t*c1.x + 3*it*t*t*c2.x + t*t*t*p3.x;
      const midY = it*it*it*p0.y + 3*it*it*t*c1.y + 3*it*t*t*c2.y + t*t*t*p3.y;
      return { x: midX - 40, y: midY - 12, width: 80, height: 24 };
    }

    // Generic fallback: bounds spanning endpoints
    const minX = Math.min(from.x, to.x);
    const minY = Math.min(from.y, to.y);
    const maxX = Math.max(from.x, to.x);
    const maxY = Math.max(from.y, to.y);
    return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
  }, [selectedEdges]);



  const bannerContainerRef = useRef<HTMLDivElement | null>(null);
  const [bannerHeight, setBannerHeight] = useState(0);

  useLayoutEffect(() => {
    const el = bannerContainerRef.current;
    if (!el) {
      setBannerHeight(0);
      return;
    }

    const updateHeight = () => setBannerHeight(el.offsetHeight);

    updateHeight();

    if (typeof ResizeObserver === 'undefined') {
      return;
    }

    const observer = new ResizeObserver(() => updateHeight());
    observer.observe(el);

    return () => observer.disconnect();
  }, []);

  return (
    <div className="h-screen flex flex-col bg-background overflow-hidden">
      {liveChartCommandGuardDialog}
      {!isEmbedMode ? (
        <div ref={bannerContainerRef} className="shrink-0">
          <DemoSignupBanner variant="app" />
        </div>
      ) : null}
      {(!isLoadingAccess && !hasAccess) ? (
        <div className="flex flex-1 items-center justify-center bg-gray-50">
          <div className="text-center">
            <div className="mb-4 text-6xl">🔒</div>
            <h1 className="mb-2 text-2xl font-bold text-gray-900">Access Denied</h1>
            <p className="mb-4 text-gray-600">
              {accessError || 'You do not have permission to view this diagram.'}
            </p>
            <button
              onClick={() => window.history.back()}
              className="rounded bg-blue-600 px-4 py-2 text-white hover:bg-blue-700"
            >
              Go Back
            </button>
          </div>
        </div>
      ) : documentError ? (
        <div className="flex flex-1 items-center justify-center bg-gray-50 px-6">
          <div className="max-w-md text-center">
            <h1 className="mb-2 text-2xl font-bold text-gray-900">Diagram could not be loaded safely</h1>
            <p className="mb-5 text-gray-600">
              Your last saved diagram has not been replaced. Check your connection and retry loading it.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="rounded bg-blue-600 px-4 py-2 text-white hover:bg-blue-700"
            >
              Retry
            </button>
          </div>
        </div>
      ) : (
        <div className="relative flex flex-1 flex-col min-h-0">
          {!isEmbedMode ? (
            <FloatingHeader awareness={awareness || undefined} offsetTop={bannerHeight + 16} />
          ) : null}

          {!isEmbedMode && isGenogramDiagram ? <ClearExampleDataBanner /> : null}

          {!isEmbedMode && diagramType === 'org' && atlasArtifactKind === 'position' ? (
            <AtlasModeChip
              mode={isHistoryPreview ? 'historical' : 'live'}
              variant="position"
              folderId={positionChart.folderId}
              draftChangeCount={liveChartDraftChangeCount}
              draftActions={liveChartDraftActions}
              onOpenWorkbench={
                shouldShowPositionStaffingWorkbench ? openPositionStaffingWorkbench : undefined
              }
            />
          ) : null}

          {/* Projection hydration also runs in planning diagrams. Chrome uses
              artifact identity, not overlapping projection-hook flags. */}
          {!isEmbedMode && diagramType === 'org' && atlasArtifactKind === 'live' ? (
            <AtlasModeChip
              mode={isHistoryPreview ? 'historical' : 'live'}
              variant="people"
              folderId={peopleChart.folderId ?? currentFolderId}
              draftChangeCount={liveChartDraftChangeCount}
              draftActions={liveChartDraftActions}
            />
          ) : null}

          {!isEmbedMode && diagramType === 'org' && atlasArtifactKind === 'planning' ? (
            <AtlasModeChip
              mode={isHistoryPreview ? 'historical' : 'draft'}
              folderId={currentFolderId}
              draftChangeCount={liveChartDraftChangeCount}
              draftActions={liveChartDraftActions}
            />
          ) : null}

          {!isEmbedMode
            && diagramType === 'org'
            && atlasArtifactKind === 'editable'
            && currentFolderId ? (
            <AtlasModeChip mode={isHistoryPreview ? 'historical' : 'freeform'} folderId={currentFolderId} />
          ) : null}

          {isLoadingAccess && (
            <div className="pointer-events-none absolute right-4 top-4 z-50 rounded-lg border bg-white/90 px-3 py-2 shadow-lg backdrop-blur-sm">
              <div className="flex items-center space-x-2">
                <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-blue-600"></div>
                <span className="text-sm text-gray-700">Checking access...</span>
              </div>
            </div>
          )}

          <div
            className="grid flex-1 min-h-0 overflow-hidden"
            style={{ gridTemplateColumns: canvasProjectionGridTemplateColumns }}
          >
            <div
              ref={canvasContainerRef}
              className="relative min-h-0 overflow-hidden bg-canvas canvas-grid"
            >
              {isLoadingAccess && (
                <MainLoader
                  isVisible={true}
                  description={tCommon('status.loadingWorkspace') as string}
                  staticLogo={false}
                />
              )}

              {isSynced && effectiveShowUseCaseSelection && !isEmbedMode ? (
                <UseCaseSelectionView onStartBlank={handleStartBlank} />
              ) : isSynced && effectiveShowTemplatePicker && !isEmbedMode ? (
                <TemplatePickerView
                  useCase={selectedUseCase!}
                  onSelectTemplate={handleSelectTemplate}
                  onGenerateFromText={handleGenerateFromText}
                  onStartBlank={handleStartBlank}
                  onBackToUseCase={handleBackToUseCaseSelection}
                />
              ) : isSynced && showFlowchartStartPicker && !isEmbedMode ? (
                SHOW_PROCESS_USE_CASE_PICKER ? (
                  <ProcessUseCasePicker
                    onSelectStarter={flowchartPicker.onSelectStarter}
                    onStartBlank={flowchartPicker.onStartBlank}
                    onGenerateFromText={flowchartPicker.onGenerateFromText}
                  />
                ) : (
                  <FlowchartStartPicker
                    onSelectStarter={flowchartPicker.onSelectStarter}
                    onStartBlank={flowchartPicker.onStartBlank}
                    onGenerateFromText={flowchartPicker.onGenerateFromText}
                  />
                )
              ) : isSynced && showOrgStartPicker && !isEmbedMode ? (
                <OrgChartStartPicker
                  onSelectStarter={orgPicker.onSelectStarter}
                  onGenerateFromPrompt={orgPicker.onGenerateFromPrompt}
                  isGeneratingPrompt={orgPicker.generatingPrompt}
                  folderId={currentFolderId}
                />
              ) : (
                <DiagramCanvasWithTransform
                  stageRef={stageRef}
                  layerRef={layerRef}
                  eventBus={eventBusRef.current}
                  commandManager={commandManagerRef.current}
                  toolRegistry={toolRegistry || undefined}
                  diagramType={diagramType}
                  awareness={awareness || undefined}
                  doc={doc}
                  updatePreview={updatePreview}
                  getShapeState={getShapeState}
                  setUserSelection={setUserSelection}
                  userInfo={userInfo}
                  canWrite={canWrite}
                  userRole={userRole}
                  entitlements={entitlements}
                  isPositionChart={isPositionChart}
                  isPlanningChart={isPlanningChart}
                  projectionLocked={isSavedAtlasView || ((isPeopleChart || isPositionChart) && !isEditingCanvasDraft)}
                  isSavedAtlasView={isSavedAtlasView}
                  isSceneScopedAtlasView={peopleChart.isSceneScoped}
                  lockProjectedNodePlacement={isSavedAtlasView || ((isPeopleChart || isPositionChart) && !isEditingCanvasDraft)}
                  onRenderedSelectionBoundsChange={handleRenderedSelectionBoundsChange}
                />
              )}
              {/* Canvas Loading Overlay */}
              <CanvasLoadingOverlay show={!orgProjectionLoadFailed && !!showCanvasLoadingOverlay} />
              {Boolean(orgProjectionError) && canShowOrgProjectionErrorNotice && (
                <OrgProjectionErrorNotice
                  error={orgProjectionError}
                  isShowingLiveContext={peopleChart.isShowingLiveContext || positionChart.isShowingLiveContext}
                  onRetry={() => window.location.reload()}
                  onOpenWorkbench={isPositionChart ? openPositionStaffingWorkbench : undefined}
                />
              )}
              <CanvasLoadingOverlay
                show={isGeneratingSeed}
                message="Generating your diagram from your prompt…"
              />
              <GenoWizardDialog
                open={showGenoWizardDialog}
                onOpenChange={handleGenoWizardOpenChange}
                onConfirmUseCase={handleGenoWizardUseCaseConfirm}
              />
              <HierarchyChartWizardDialog
                open={showHierarchyChartWizardDialog}
                onOpenChange={handleHierarchyChartWizardOpenChange}
                onSelectChoice={handleHierarchyChartWizardChoice}
              />
          {/* Assistant Panel (left-floating) */}
              {assistantPanelKind === 'genogram' ? (
                <Suspense fallback={null}>
                  <GenogramAssistantPanel
                    canWrite={canWrite}
                    isLoadingAccess={isLoadingAccess}
                    diagramId={diagramId}
                    initialChatPrompt={onboarding.assistantPrompt}
                    initialGeneratePromptKey={onboarding.genogramSourcePromptKey}
                  />
                </Suspense>
              ) : assistantPanelKind === 'flowchart' ? (
                <ProcessMappingAssistantPanel
                  canWrite={canWrite}
                  isLoadingAccess={isLoadingAccess}
                  initialComposePrefill={flowchartAssistantPrefill}
                />
              ) : assistantPanelKind === 'org' ? (
                <Suspense fallback={null}>
                  <OrgChartAssistantPanel canWrite={canWrite} isLoadingAccess={isLoadingAccess} />
                </Suspense>
              ) : assistantPanelKind === 'network' ? (
                <Suspense fallback={null}>
                  <NetworkAssistantPanel canWrite={canWrite} isLoadingAccess={isLoadingAccess} />
                </Suspense>
              ) : null}
          {/* Legend Overlay */}
              {!isEmbedMode && isOrg && <ColorLegend canSetDefault={canWrite} />}
              {!isEmbedMode && isFlowchartDiagram && (
                <div className="pointer-events-none absolute bottom-20 right-4 z-30 flex flex-col items-end gap-2">
                  <ColorLegend stacked />
                  <ProcessOverlayLegend stacked />
                </div>
              )}
              {!isEmbedMode && isGenogramDiagram && <GenogramHealthFocusLegend />}

          {/* Collaborative Cursors */}
              {!isEmbedMode && awareness && (
                <CollaborativeCursors
                  awareness={awareness}
                  containerRef={canvasContainerRef}
                  stageRef={stageRef}
                />
              )}

          {/* Shape Toolbar - appears above selected shapes */}
              {!isEmbedMode && !isPositionChart && selectedPersons.length > 0 && hasRenderedOrgSelectionBounds && toolRegistry && (
                onlyTablesSelected ? (
                  <TableShapeToolbar
                    selectedShapes={selectedTables}
                    selectionBounds={toolbarSelectionBounds}
                    toolRegistry={toolRegistry}
                    toolContext={toolContext}
                    onToolExecute={async (toolId, params) => {
                      const allowWrite = canWriteRef.current || (typeof window !== 'undefined' && (window as any).__testCanWriteOverride === true);
                      if (!allowWrite) return;
                      if (toolRegistry) {
                        try {
                          await toolRegistry.executeTool(toolId, params);
                        } catch (error) {
                          appLogger.error(`Failed to execute contextual tool ${toolId}:`, error);
                        }
                      }
                    }}
                  />
                ) : (
                  <ShapeToolbar
                    selectedShapes={selectedPersons}
                    selectionBounds={toolbarSelectionBounds}
                    toolRegistry={toolRegistry}
                    toolContext={toolContext}
                    onToolExecute={async (toolId, params) => {
                      const allowWrite = canWriteRef.current || (typeof window !== 'undefined' && (window as any).__testCanWriteOverride === true);
                      if (!allowWrite) return;
                      if (toolRegistry) {
                        try {
                          await toolRegistry.executeTool(toolId, params);
                        } catch (error) {
                          appLogger.error(`Failed to execute contextual tool ${toolId}:`, error);
                        }
                      }
                    }}
                  />
                )
              )}

          {/* Mirrors the open diagram's Compliance layer into the canvas
              layer store so GrcBadges + the assurance inspector can gate
              on it. Renders null. */}
              <ProcessCanvasLayerSync />

          {/* Assurance inspector — anchors below the selection box and
              surfaces controls / evidence / risk counts for the node.
              No-ops when there's no assurance data, so it's safe to
              mount unconditionally alongside the ShapeToolbar. */}
              {!isEmbedMode && !isPositionChart && selectedPersons.length > 0 && (
                <AssuranceSelectionInspector
                  selectedShapes={selectedPersons}
                  selectionBounds={selectionBounds}
                />
              )}

          {/* Connector Toolbar - appears when an edge is selected */}
              {!isEmbedMode && !isPositionChart && selectedPersons.length === 0 && selectedEdges.length > 0 && (
            <ConnectorToolbar selectedEdges={selectedEdges as Array<{ id: string }>} selectionBounds={edgeBounds} toolContext={toolContext} />
              )}

          {/* Bottom Floating Toolbar – hidden for viewers and onboarding screens */}
              {!isEmbedMode && !isPositionChart && canWrite && !isSavedAtlasView && !effectiveShowUseCaseSelection && !effectiveShowTemplatePicker && (
                <BottomToolbar
                  zoom={stageScale}
                  onZoomIn={handleZoomIn}
                  onZoomOut={handleZoomOut}
                  onZoomTo={handleZoomToScale}
                  onZoomReset={handleZoomResetView}
                  onZoomFit={handleZoomToFit}
                  toolRegistry={toolRegistry || undefined}
                  toolContext={toolContext}
                  onToolExecute={async (toolId, params) => {
                    if (!canWriteRef.current) return;
                    if (toolRegistry) {
                      try {
                        await toolRegistry.executeTool(toolId, params);
                      } catch (error) {
                        appLogger.error(`Failed to execute tool ${toolId}:`, error);
                      }
                    }
                  }}
                  onUndo={handleUndo}
                  onRedo={handleRedo}
                  canUndo={canUndo}
                  canRedo={canRedo}
                  hiddenTools={hiddenTools}
                />
              )}

          {/* Read-only and saved views retain navigation without authoring tools. */}
              {shouldShowStandaloneZoomToolbar({
                isEmbedMode,
                isPositionChart,
                canWrite,
                isSavedAtlasView,
                showUseCaseSelection: effectiveShowUseCaseSelection,
                showTemplatePicker: effectiveShowTemplatePicker,
              }) && (
                <ZoomToolbar
                  zoom={stageScale}
                  onZoomIn={handleZoomIn}
                  onZoomOut={handleZoomOut}
                  onZoomTo={handleZoomToScale}
                  onZoomReset={handleZoomResetView}
                  onZoomFit={handleZoomToFit}
                  showNavigationUtilities
                />
              )}

          {/* Embed preview: zoom controls only */}
              {isEmbedMode && !effectiveShowUseCaseSelection && !effectiveShowTemplatePicker && (
                <ZoomToolbar
                  zoom={stageScale}
                  onZoomIn={handleZoomIn}
                  onZoomOut={handleZoomOut}
                  onZoomTo={handleZoomToScale}
                  onZoomReset={handleZoomResetView}
                  onZoomFit={handleZoomToFit}
                />
              )}

              {!isEmbedMode && isPositionChart && (
                <ZoomToolbar
                  zoom={stageScale}
                  onZoomIn={handleZoomIn}
                  onZoomOut={handleZoomOut}
                  onZoomTo={handleZoomToScale}
                  onZoomReset={handleZoomResetView}
                  onZoomFit={handleZoomToFit}
                />
              )}

              {shouldShowPositionStaffingWorkbench && <PositionStaffingWorkbenchToggle />}

          {/* Render toolbar components for tools that have them (e.g., shape picker, sticky note) */}
              {!isEmbedMode && toolRegistry && toolContext && (
                <Fragment>
                  {/* Keep toolbar popovers mounted while late tool registration settles. */}
                  {['shape-picker', 'sticky-note', 'pen'].filter(id => !hiddenTools.includes(id)).map((toolId) => {
                    const tool = toolRegistry.getTool(toolId);
                    if (!tool) return null;

                    const ToolbarComponent = (tool as any).getToolbarComponent?.();
                    if (!ToolbarComponent) return null;

                    return (
                      <ToolbarComponent
                        key={toolId}
                        tool={tool}
                        context={toolContext}
                        onExecute={async (params: any) => {
                          const allowWrite = canWriteRef.current || (typeof window !== 'undefined' && (window as any).__testCanWriteOverride === true);
                          if (!allowWrite) return;
                          try {
                            await toolRegistry.executeTool(toolId, params);
                          } catch (error) {
                            appLogger.error(`Failed to execute tool ${toolId}:`, error);
                          }
                        }}
                      />
                    );
                  })}
                </Fragment>
              )}
            </div>
            {!isEmbedMode && activeCanvasProjection ? (
              <CanvasProjectionSplitHandle
                ratio={canvasProjectionSplitRatio}
                onRatioChange={setCanvasProjectionSplitRatio}
              />
            ) : null}
            {!isEmbedMode && activeCanvasProjection ? (
              <div
                className="relative min-h-0 overflow-hidden bg-canvas canvas-grid"
              >
                <CanvasProjectionPaneHost
                  diagramId={diagramId}
                  diagramType={diagramType}
                />
              </div>
            ) : null}

        {/* Floating Properties toolbar + card (compact, leaves top toolbar space) */}
            {!isEmbedMode && !isPositionChart ? (
              <FloatingProperties
                toolRegistry={toolRegistry || undefined}
                toolContext={toolRegistry ? toolContext : undefined}
                offsetTop={bannerHeight > 0 ? bannerHeight + 16 : 24}
                rightOffset={floatingPropertiesRightOffset}
                onToolExecute={async (toolId, params) => {
                  const allowWrite = canWriteRef.current || (typeof window !== 'undefined' && (window as any).__testCanWriteOverride === true);
                  if (!allowWrite) return;
                  if (toolRegistry) {
                    try {
                      await toolRegistry.executeTool(toolId, params);
                    } catch (error) {
                      appLogger.error(`Failed to execute property tool ${toolId}:`, error);
                      toast.error(
                        error instanceof Error && error.message ? error.message : 'This change could not be made.',
                        { duration: 6000 },
                      );
                    }
                  }
                }}
              />
            ) : null}
          </div>
          {!isEmbedMode ? <DiagramLinkPreviewModal /> : null}
        </div>
      )}

    </div>
  );
}

// Main app component wrapped with provider
function App() {
  const isEmbedMode = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('embed') === '1';
  return isEmbedMode ? <AppEmbed /> : <AppFull />;
}

function AppEmbed() {
  useMeQuery();
  const routeDiagramId = (window as unknown as { __DIAGRAM_ID__?: string }).__DIAGRAM_ID__ ?? 'demo-diagram';
  const gravityUid = getGravityUserId();
  const userId = gravityUid || 'anonymous';
  const userName = 'Anonymous User';

  return (
    <PopupManagerProvider>
      <DiagramDocumentProvider 
        diagramId={routeDiagramId} 
        userId={userId}
        userName={userName}
      >
        <ShapeLimitProvider diagramId={routeDiagramId}>
          <DiagramEditor />
        </ShapeLimitProvider>
      </DiagramDocumentProvider>
    </PopupManagerProvider>
  );
}

function AppFull() {
  const routeDiagramId = (window as unknown as { __DIAGRAM_ID__?: string }).__DIAGRAM_ID__ ?? 'demo-diagram';
  const { data: me } = useMeQuery();
  const gravityUid = getGravityUserId();
  const userId = gravityUid || me?.id || 'anonymous';
  const userName = me?.name || 'Anonymous User';
  const { isOpen, feature, reason, templateCategory, descriptor, closeModal, openModal } = useUpgradeModalStore();
  usePostHogIdentify();

  // Check for toast message stored in localStorage (e.g., from legacy import)
  // Use a ref to track shown toasts across re-renders and remounts
  const shownToastKeysRef = useRef<Set<string>>(new Set());
  
  useEffect(() => {
    if (!routeDiagramId || routeDiagramId === 'demo-diagram') return;
    
    const toastKey = `diagram-toast:${routeDiagramId}`;
    
    // Skip if we've already shown this toast (persists across remounts)
    if (shownToastKeysRef.current.has(toastKey)) {
      return;
    }
    
    const checkAndShowToast = () => {
      const toastData = window.localStorage.getItem(toastKey);
      
      if (toastData) {
        try {
          const { message, type } = JSON.parse(toastData);
          
          // Mark as shown immediately and remove from localStorage to prevent duplicates
          shownToastKeysRef.current.add(toastKey);
          window.localStorage.removeItem(toastKey);
          
          // Show toast after a short delay to ensure the page is loaded
          setTimeout(() => {
            if (type === 'success') {
              toast.success(message, { duration: 6000 }); // Show for 6 seconds
            } else if (type === 'error') {
              toast.error(message, { duration: 6000 }); // Show for 6 seconds
            }
          }, 1000);
        } catch (error) {
          appLogger.warn('Failed to parse toast message from localStorage', error);
          window.localStorage.removeItem(toastKey);
        }
      }
    };
    
    // Check immediately
    checkAndShowToast();
    
    // Also listen for storage events (in case message is set after mount)
    // Note: storage events only fire for changes from OTHER tabs/windows, not the current one
    const handleStorage = (e: StorageEvent) => {
      if (e.key === toastKey && e.newValue && !shownToastKeysRef.current.has(toastKey)) {
        checkAndShowToast();
      }
    };
    
    window.addEventListener('storage', handleStorage);
    
    return () => {
      window.removeEventListener('storage', handleStorage);
    };
  }, [routeDiagramId]);

  const mapFeatureToPage = useCallback(
    (value?: string): React.ComponentProps<typeof UpgradeModal>['page'] => {
      if (!value) return 'generic';
      if (value.startsWith('export.')) return 'export';
      if (value === 'sharing.invite_by_email' || value === 'sharing.email_invites' || value === 'sharing.guests') {
        return 'sharing.invite_by_email';
      }
      if (value.startsWith('shapes.')) return 'shapes.unlimited';
      if (value === 'ai.templates' || value === 'ai.templates.access') return 'ai-templates';
      if (value === 'premium-templates') return 'premium-templates';
      if (value === 'create-folder') return 'create-folder';
      if (value === 'archived-folder') return 'archived-folder';
      if (value === 'create-workspace') return 'create-workspace';
      return 'generic';
    },
    []
  );

  const upgradePage = mapFeatureToPage(feature);

  // Listen for plan:blocked events from tools
  useEffect(() => {
    const handlePlanBlocked = (event: CustomEvent<{ reason?: string; feature?: string }>) => {
      const { reason: eventReason, feature: eventFeature } = event.detail ?? {};
      openModal(eventFeature || 'shapes.create', eventReason || 'This feature requires a plan upgrade');
    };

    // Listen for plan:blocked events on the window (global event bus)
    window.addEventListener('plan:blocked', handlePlanBlocked as EventListener);
    
    return () => {
      window.removeEventListener('plan:blocked', handlePlanBlocked as EventListener);
    };
  }, [openModal]);


  return (
    <PopupManagerProvider>
      <OnboardingDialog />
      <DiagramDocumentProvider 
        diagramId={routeDiagramId} 
        userId={userId}
        userName={userName}
      >
        <ShapeLimitProvider diagramId={routeDiagramId}>
          <DiagramEditor />
        </ShapeLimitProvider>
      </DiagramDocumentProvider>
      <UpgradeModal 
        isOpen={isOpen}
        onClose={closeModal}
        feature={feature}
        reason={reason}
        descriptor={descriptor}
        page={upgradePage}
        templateCategory={templateCategory}
      />
    </PopupManagerProvider>
  );
}

export default App;
